import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Lock, Plus, X, Calendar, Users, LayoutGrid, CalendarDays, Edit2, Trash2, MessageCircle, GitBranch, Printer, Clock, DollarSign, CheckCircle2, AlertCircle, Search, Phone, Camera, Package } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Textarea } from "@/components/ui/textarea";
import { useSwipeNavigation } from "@/hooks/useSwipeNavigation";
import { AppointmentPhotoGallery } from "@/components/AppointmentPhotoGallery";
import { AuthPinModal } from "@/components/AuthPinModal";
import { useIsMobile } from "@/hooks/useMobile";
import { getMobileAgendaNowScrollTop, getMobileAgendaQuickStartSlot, getMobileAppointmentTimingSignal } from "@shared/mobileOperations";
import { brasiliaDateString, fromBrasiliaDateTime } from "@shared/businessTimezone";
import { bookingOriginHoverLabel, bookingOriginPresentation, resolveBookingOrigin } from "@shared/bookingOrigin";
import { MobileAgendaActions } from "@/components/MobileOperationControls";
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
} from "@dnd-kit/core";

function formatTanningArrivalIntentForAgenda(intent: { preferredDate: string; preferredTime?: string | null; preferredPeriod?: string | null }) {
  const [year, month, day] = intent.preferredDate.split("-");
  const dateLabel = year && month && day ? `${day}/${month}` : intent.preferredDate;
  if (intent.preferredTime) return `${dateLabel} · previsão: ${intent.preferredTime}`;
  const period = { manha: "manhã", tarde: "tarde", noite: "noite" }[intent.preferredPeriod ?? ""];
  return period ? `${dateLabel} · previsão: ${period}` : dateLabel;
}

function formatCompactAgendaDayHeader(date: Date) {
  const weekday = date.toLocaleDateString("pt-BR", { weekday: "long" });
  const month = date.toLocaleDateString("pt-BR", { month: "short" }).replace(".", "");
  const year = date.toLocaleDateString("pt-BR", { year: "2-digit" });
  return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)} · ${date.getDate()} · ${month.charAt(0).toUpperCase()}${month.slice(1)}/${year}`;
}

const HOURS = Array.from({ length: 14 }, (_, i) => i + 7); // 7h às 20h
const SLOT_HEIGHT = 64; // px por hora
const AGENDA_DENSITIES = {
  compact: { label: "Compacta", slotHeight: 46 },
  standard: { label: "Padrão", slotHeight: 64 },
  detailed: { label: "Detalhada", slotHeight: 82 },
} as const;
type AgendaDensity = keyof typeof AGENDA_DENSITIES;
const AGENDA_DENSITY_KEY = "agenda-density-v1";

/** Formata uma data como YYYY-MM-DD usando o fuso local (evita offset UTC) */
function toLocalDateStr(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function getWeekDates(baseDate: Date) {
  const d = new Date(baseDate);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  // Resetar horas para meia-noite local para evitar problemas de fuso
  const monday = new Date(d.setDate(diff));
  monday.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    return date;
  });
}

const DAY_NAMES = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

// ─── Histórico de Alterações ─────────────────────────────────────────────────
function AppointmentHistorySection({ appointmentId }: { appointmentId?: number }) {
  const [open, setOpen] = useState(false);
  const { data: history, isLoading } = (trpc as any).appointments.getHistory.useQuery(
    { appointmentId: appointmentId ?? 0 },
    { enabled: !!appointmentId && open }
  );
  if (!appointmentId) return null;
  const fieldFormatters: Record<string, (v: string) => string> = {
    startTime: (v) => v ? new Date(v).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : v,
    endTime: (v) => v ? new Date(v).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : v,
    totalPrice: (v) => v ? `R$ ${Number(v).toFixed(2)}` : v,
  };
  const statusLabels: Record<string, string> = {
    pending: "Pendente", confirmed: "Confirmado", completed: "Concluído", cancelled: "Cancelado", no_show: "Não compareceu",
    unpaid: "Não pago", paid: "Pago", refunded: "Reembolsado",
    dinheiro: "Dinheiro", pix: "PIX", credito: "Cartão de crédito", debito: "Cartão de débito", voucher: "Voucher", outro: "Outro",
  };
  const fmt = (field: string, val: string | null) => {
    if (!val) return "(vazio)";
    const f = fieldFormatters[field];
    const labeled = statusLabels[val];
    return labeled ?? (f ? f(val) : val);
  };
  return (
    <div className="mt-4 border-t border-border pt-3">
      <button
        type="button"
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full"
        onClick={() => setOpen((o) => !o)}
      >
        <GitBranch className="w-4 h-4" />
        <span className="font-medium">Histórico de alterações</span>
        <span className="ml-auto text-xs">{open ? "▲ Ocultar" : "▼ Ver"}</span>
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {isLoading && <p className="text-xs text-muted-foreground py-2">Carregando...</p>}
          {!isLoading && (!history || history.length === 0) && (
            <p className="text-xs text-muted-foreground py-2 text-center">Nenhuma alteração registrada ainda.</p>
          )}
          {!isLoading && history && history.length > 0 && (
            <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
              {history.map((entry: any) => (
                <div key={entry.id} className="flex gap-2 text-xs rounded-md bg-muted/40 px-2.5 py-2">
                  <div className="shrink-0 mt-0.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-foreground">
                      <span className="font-medium">{entry.fieldChanged}</span>:
                      {entry.oldValue && <> <span className="line-through text-muted-foreground">{fmt(entry.fieldChanged, entry.oldValue)}</span> →</>}
                      {" "}<span className="text-primary font-medium">{fmt(entry.fieldChanged, entry.newValue)}</span>
                    </p>
                    <p className="text-muted-foreground mt-0.5">
                      {entry.changedByName ?? "Sistema"} · {new Date(entry.createdAt).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatTime(date: Date) {
  return new Date(date).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function getTopPx(date: Date, startHour: number, slotHeight = SLOT_HEIGHT) {
  const h = new Date(date).getHours() + new Date(date).getMinutes() / 60;
  return (h - startHour) * slotHeight;
}

function getHeightPx(startTime: Date, endTime: Date, slotHeight = SLOT_HEIGHT) {
  const diffMs = new Date(endTime).getTime() - new Date(startTime).getTime();
  const diffH = diffMs / 3600000;
  return Math.max(diffH * slotHeight, 24);
}

// ─── Overlap Layout Algorithm ───────────────────────────────────────────────
// Distribui agendamentos sobrepostos em sub-colunas para que todos fiquem visíveis
function computeColumns(appts: any[]): Map<number, { col: number; totalCols: number }> {
  // Ordena por hora de início
  const sorted = [...appts].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  const result = new Map<number, { col: number; totalCols: number }>();
  // Grupos de sobrepostos
  const groups: any[][] = [];
  for (const appt of sorted) {
    const apptStart = new Date(appt.startTime).getTime();
    const apptEnd = new Date(appt.endTime).getTime();
    let placed = false;
    for (const group of groups) {
      // Verifica se há sobreposição com qualquer evento do grupo
      const overlaps = group.some((g: any) => {
        const gStart = new Date(g.startTime).getTime();
        const gEnd = new Date(g.endTime).getTime();
        return apptStart < gEnd && apptEnd > gStart;
      });
      if (overlaps) {
        group.push(appt);
        placed = true;
        break;
      }
    }
    if (!placed) groups.push([appt]);
  }
  // Para cada grupo, atribui colunas
  for (const group of groups) {
    const cols: number[] = []; // último endTime de cada coluna
    const colAssign: number[] = [];
    for (const appt of group) {
      const apptStart = new Date(appt.startTime).getTime();
      const apptEnd = new Date(appt.endTime).getTime();
      let assigned = -1;
      for (let c = 0; c < cols.length; c++) {
        if (apptStart >= cols[c]!) {
          assigned = c;
          cols[c] = apptEnd;
          break;
        }
      }
      if (assigned === -1) {
        assigned = cols.length;
        cols.push(apptEnd);
      }
      colAssign.push(assigned);
    }
    const totalCols = cols.length;
    group.forEach((appt: any, i: number) => {
      result.set(appt.id, { col: colAssign[i]!, totalCols });
    });
  }
  return result;
}

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-amber-400/80 border-amber-500 text-amber-950",
  confirmed: "bg-emerald-400/80 border-emerald-500 text-emerald-950",
  in_service: "bg-violet-400/80 border-violet-500 text-violet-950",
  completed: "bg-blue-400/80 border-blue-500 text-blue-950",
  cancelled: "bg-red-300/60 border-red-400 text-red-900",
  no_show: "bg-gray-300/60 border-gray-400 text-gray-700",
};

// ─── Draggable Appointment Card ───────────────────────────────────────────────
function DraggableApptCard({
  appt,
  profName,
  profAvatarUrl,
  profColor,
  startHour,
  onEdit,
  onOpenCheckout,
  onRegisterArrival,
  onUpdateStatus,
  col = 0,
  totalCols = 1,
  isExtraExecutor = false,
  executorServiceName,
  salonId,
  slotHeight = SLOT_HEIGHT,
  density = "standard",
  currentTime,
}: {
  appt: any;
  profName: string;
  profAvatarUrl?: string | null;
  profColor?: string | null;
  startHour: number;
  onEdit: (appt: any) => void;
  onOpenCheckout?: (apptId: number) => void;
  onRegisterArrival?: (apptId: number) => void;
  onUpdateStatus?: (apptId: number, status: "confirmed" | "in_service" | "completed" | "no_show") => void;
  col?: number;
  totalCols?: number;
  isExtraExecutor?: boolean;
  executorServiceName?: string;
  salonId?: number;
  slotHeight?: number;
  density?: AgendaDensity;
  currentTime: Date;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `appt-${appt.id}`,
    data: { appt },
  });

  // Mobile: long press abre popup de detalhes; toque simples abre edição
  const [touchPopupOpen, setTouchPopupOpen] = useState(false);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchMoved = useRef(false);

  function handleTouchStart() {
    touchMoved.current = false;
    longPressTimer.current = setTimeout(() => {
      if (!touchMoved.current) setTouchPopupOpen(true);
    }, 500);
  }
  function handleTouchMove() {
    touchMoved.current = true;
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  }
  function handleTouchEnd() {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  }

  const top = getTopPx(new Date(appt.startTime), startHour, slotHeight);
  const height = getHeightPx(new Date(appt.startTime), new Date(appt.endTime), slotHeight);
  const colorClass = STATUS_COLORS[appt.status] ?? "bg-slate-400/80 border-slate-500";

  // Posicionamento lateral para evitar sobreposição
  const colWidthPct = 100 / totalCols;
  const leftPct = col * colWidthPct;
  const widthPct = colWidthPct;
  // Pequena margem interna para separar visualmente os cards
  const marginPx = totalCols > 1 ? 1 : 2;

  const statusLabel: Record<string, string> = {
    scheduled: "Agendado",
    pending: "Pendente",
    confirmed: "Confirmado",
    in_service: "Em atendimento",
    completed: "Concluído",
    cancelled: "Cancelado",
    no_show: "Não compareceu",
    waiting: "Aguardando",
    in_progress: "Em andamento",
  };
  const paymentStatusLabel: Record<string, string> = {
    pending: "Pendente",
    paid: "Pago",
    partial: "Parcial",
    cancelled: "Cancelado",
  };
  const bookingOrigin = resolveBookingOrigin(appt);
  const bookingOriginInfo = bookingOriginPresentation[bookingOrigin];
  const bookingOriginHover = bookingOriginHoverLabel(bookingOrigin, appt.createdByName);
  const timingSignal = getMobileAppointmentTimingSignal({
    status: appt.status,
    startTime: appt.startTime,
    endTime: appt.endTime,
  }, currentTime);
  const timingTone = timingSignal?.tone === "active"
    ? "ring-2 ring-blue-600/80"
    : timingSignal?.tone === "overdue"
      ? "ring-2 ring-red-600/80"
      : timingSignal?.tone === "upcoming"
        ? "ring-2 ring-amber-500/80"
        : "";

  return (
    <>
    {/* Dialog mobile de detalhes (long press) */}
    <Dialog open={touchPopupOpen} onOpenChange={setTouchPopupOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-sm rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            {appt.clientPhotoUrl ? (
              <img src={appt.clientPhotoUrl} className="w-9 h-9 rounded-full object-cover border border-border" />
            ) : (
              <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0" style={{ backgroundColor: profColor ?? "#C9A96E" }}>
                {(appt.clientName ?? "C").charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <p className="font-semibold truncate">{appt.clientName ?? "Cliente"}</p>
              {appt.clientPhone && <p className="text-xs text-muted-foreground font-normal">{appt.clientPhone}</p>}
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
              <span className="font-medium">{appt.serviceName ?? "Serviço"}</span>
            </div>
            {appt.extraServices?.map((es: any, i: number) => (
              <div key={i} className="flex items-center gap-2 pl-1 text-muted-foreground">
                <span>+</span>
                <span>{es.name}{es.professionalName ? ` (${es.professionalName})` : ""}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-muted-foreground shrink-0" />
            <span>{profName}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
            <span>{formatTime(new Date(appt.startTime))} – {formatTime(new Date(appt.endTime))}</span>
          </div>
          {appt.totalPrice && (
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="font-semibold">R$ {Number(appt.totalPrice).toFixed(2)}</span>
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${bookingOriginInfo.tone}`} title={bookingOriginHover}>
              Origem: {bookingOriginInfo.label}
            </span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">
              {statusLabel[appt.status] ?? appt.status}
            </span>
            {timingSignal && <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${timingSignal.tone === "active" ? "bg-blue-100 text-blue-800" : timingSignal.tone === "overdue" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"}`}>{timingSignal.label}</span>}
            {appt.paymentStatus && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                appt.paymentStatus === "paid" ? "bg-green-100 text-green-800" :
                appt.paymentStatus === "pending" ? "bg-yellow-100 text-yellow-800" :
                "bg-secondary text-secondary-foreground"
              }`}>
                {paymentStatusLabel[appt.paymentStatus] ?? appt.paymentStatus}
              </span>
            )}
          </div>
          {appt.notes && <p className="text-xs text-muted-foreground italic border-t pt-2">{appt.notes}</p>}
          {timingSignal && <p className={`text-xs rounded-md px-2.5 py-2 ${timingSignal.tone === "overdue" ? "bg-red-50 text-red-800" : timingSignal.tone === "active" ? "bg-blue-50 text-blue-800" : "bg-amber-50 text-amber-800"}`}>{timingSignal.description}</p>}
          {/* Fotos de Procedimento */}
          {salonId && (
            <div className="border-t pt-3">
              <div className="flex items-center gap-1.5 mb-2">
                <Camera className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Fotos do Procedimento</span>
              </div>
              <AppointmentPhotoGallery appointmentId={appt.id} salonId={salonId} canEdit={true} />
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 flex-col sm:flex-row">
          <Button variant="outline" className="flex-1" onClick={() => setTouchPopupOpen(false)}>Fechar</Button>
          <Button variant="secondary" className="flex-1" onClick={() => { setTouchPopupOpen(false); onEdit(appt); }}>Editar</Button>
          {onRegisterArrival && !["cancelled", "completed", "no_show"].includes(appt.status) && (
            <Button variant="outline" className="flex-1 border-primary/30 text-primary" onClick={() => { setTouchPopupOpen(false); onRegisterArrival(appt.id); }}>
              <Users className="w-4 h-4 mr-1" />Chegada
            </Button>
          )}
          {onUpdateStatus && appt.status === "pending" && (
            <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { setTouchPopupOpen(false); onUpdateStatus(appt.id, "confirmed"); }}>
              <CheckCircle2 className="w-4 h-4 mr-1" />Confirmar
            </Button>
          )}
          {onUpdateStatus && appt.status === "confirmed" && (
            <Button className="flex-1 bg-violet-600 hover:bg-violet-700 text-white" onClick={() => { setTouchPopupOpen(false); onUpdateStatus(appt.id, "in_service"); }}>
              <CheckCircle2 className="w-4 h-4 mr-1" />Em atendimento
            </Button>
          )}
          {onUpdateStatus && appt.status === "in_service" && (
            <Button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white" onClick={() => { setTouchPopupOpen(false); onUpdateStatus(appt.id, "completed"); }}>
              <CheckCircle2 className="w-4 h-4 mr-1" />Concluir
            </Button>
          )}
          {onUpdateStatus && ["pending", "confirmed", "in_service"].includes(appt.status) && (
            <Button variant="outline" className="flex-1 border-destructive/30 text-destructive" onClick={() => { setTouchPopupOpen(false); onUpdateStatus(appt.id, "no_show"); }}>
              Faltou
            </Button>
          )}
          {onOpenCheckout && appt.paymentStatus !== "paid" && (
            <Button className="flex-1 bg-green-600 hover:bg-green-700 text-white" onClick={() => { setTouchPopupOpen(false); onOpenCheckout(appt.id); }}>
              <DollarSign className="w-4 h-4 mr-1" />Caixa
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <TooltipProvider delayDuration={400}>
      <Tooltip>
        <TooltipTrigger asChild>
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`absolute rounded border ${colorClass} ${density === "compact" ? "px-0.5 py-px" : density === "detailed" ? "px-1.5 py-1" : "px-1 py-0.5"} overflow-hidden cursor-grab active:cursor-grabbing hover:opacity-90 transition-opacity select-none ${isDragging ? "opacity-30" : ""} ${isExtraExecutor ? "border-dashed border-2 opacity-85" : ""} ${timingTone}`}
      style={{
        top,
        height,
        zIndex: isDragging ? 0 : 10,
        left: `calc(${leftPct}% + ${marginPx}px)`,
        width: `calc(${widthPct}% - ${marginPx * 2}px)`,
      }}
      onDoubleClick={(e) => { e.stopPropagation(); onEdit(appt); }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      data-appt-card="true"
    >
      {/* Linha 1: avatar + nome do cliente + badge extra */}
      <div className="flex items-center gap-1">
        {/* Prioridade: foto do cliente > inicial do cliente */}
        {appt.clientPhotoUrl ? (
          <img
            src={appt.clientPhotoUrl}
            alt={appt.clientName ?? "Cliente"}
            className="w-4 h-4 rounded-full object-cover shrink-0 border border-white/40"
          />
        ) : (
          <div
            className="w-4 h-4 rounded-full shrink-0 flex items-center justify-center text-[7px] font-bold text-white border border-white/40"
            style={{ backgroundColor: profColor ?? "#C9A96E" }}
          >
            {(appt.clientName ?? "C").charAt(0).toUpperCase()}
          </div>
        )}
        <p className="text-[10px] font-semibold leading-tight flex-1 min-w-0" style={{ overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical" }}>
          {appt.clientName ?? "Cliente"}
        </p>
        {/* Badge de serviço extra: indica que este profissional é executor parcial */}
        {isExtraExecutor && (
          <span className="shrink-0 text-[7px] font-bold px-0.5 py-px rounded bg-white/30 text-white leading-none border border-white/50" title="Serviço extra">
            +S
          </span>
        )}
        <span className="shrink-0 text-[7px] font-bold px-0.5 py-px rounded bg-white/30 text-white leading-none border border-white/50" title={bookingOriginHover}>
          {bookingOriginInfo.compact}
        </span>
        {timingSignal && <span className={`shrink-0 text-[8px] font-bold leading-none ${timingSignal.tone === "overdue" ? "text-red-950" : "text-white"}`} title={timingSignal.label} aria-label={timingSignal.label}>{timingSignal.compact}</span>}
      </div>
      {/* Linha 2: horário · profissional */}
      {density !== "compact" && height > 28 && (
        <div className="mt-0.5 flex min-w-0 items-center gap-1 text-[9px] leading-tight opacity-70">
          {profAvatarUrl ? (
            <img src={profAvatarUrl} alt={`Profissional ${profName}`} className="h-3 w-3 shrink-0 rounded-full border border-white/40 object-cover" />
          ) : profName ? (
            <span aria-label={`Profissional ${profName}`} className="flex h-3 w-3 shrink-0 items-center justify-center rounded-full border border-white/40 bg-white/20 text-[6px] font-bold text-white">
              {profName.charAt(0).toUpperCase()}
            </span>
          ) : null}
          <span className="min-w-0 truncate">{formatTime(new Date(appt.startTime))} · {profName}</span>
        </div>
      )}
      {/* Linha 3: nome do serviço ou serviço executor */}
      {density !== "compact" && height > 28 && (
        <p className="text-[9px] opacity-90 font-medium leading-tight mt-0.5" style={{ overflow: "hidden", display: "-webkit-box", WebkitLineClamp: height > 55 ? 2 : 1, WebkitBoxOrient: "vertical" }}>
          {isExtraExecutor && executorServiceName ? `✂ ${executorServiceName}` : (appt.serviceName ?? "")}
        </p>
      )}
      {/* Extras e total apenas em cards maiores */}
      {density === "detailed" && height > 65 && appt.extraServices?.length > 0 && (
        <div className="mt-0.5 space-y-0.5">
          {appt.extraServices.slice(0, 2).map((es: any, i: number) => (
            <p key={i} className="text-[8px] opacity-60" style={{ overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 1, WebkitBoxOrient: "vertical" }}>
              + {es.name}
            </p>
          ))}
          {appt.totalPrice && (
            <p className="text-[9px] font-semibold opacity-90 border-t border-white/20 pt-0.5">
              R$ {Number(appt.totalPrice).toFixed(2)}
            </p>
          )}
        </div>
      )}
      {/* Preço quando não há extras */}
      {density === "detailed" && height > 65 && !appt.extraServices?.length && appt.totalPrice && (
        <p className="text-[9px] opacity-70 mt-0.5">R$ {Number(appt.totalPrice).toFixed(2)}</p>
      )}
    </div>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-xs p-3 space-y-2 z-50" sideOffset={8}>
          {/* Cliente */}
          <div className="flex items-center gap-2">
            {appt.clientPhotoUrl ? (
              <img src={appt.clientPhotoUrl} className="w-8 h-8 rounded-full object-cover border border-border" />
            ) : (
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ backgroundColor: profColor ?? "#C9A96E" }}>
                {(appt.clientName ?? "C").charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <p className="font-semibold text-sm leading-tight">{appt.clientName ?? "Cliente"}</p>
              {appt.clientPhone && <p className="text-xs text-muted-foreground">{appt.clientPhone}</p>}
            </div>
          </div>
          <hr className="border-border" />
          {/* Serviço */}
          <div className="space-y-1">
            {isExtraExecutor && (
              <div className="flex items-center gap-1.5 text-xs">
                <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 font-semibold text-[10px]">
                  ✂ Executor de serviço extra
                </span>
              </div>
            )}
            <div className="flex items-center gap-1.5 text-xs">
              <CheckCircle2 className="w-3 h-3 text-primary shrink-0" />
              <span className="font-medium">{isExtraExecutor && executorServiceName ? executorServiceName : (appt.serviceName ?? "Serviço")}</span>
            </div>
            {!isExtraExecutor && appt.extraServices?.length > 0 && appt.extraServices.map((es: any, i: number) => (
              <div key={i} className="flex items-center gap-1.5 text-xs pl-1">
                <span className="text-muted-foreground">+</span>
                <span>{es.name}{es.professionalName ? ` (${es.professionalName})` : ""}</span>
              </div>
            ))}
            {isExtraExecutor && (
              <div className="flex items-center gap-1.5 text-xs pl-1 text-muted-foreground">
                <span className="text-[10px]">Serviço principal: {appt.serviceName ?? ""} ({appt.clientName ?? "Cliente"})</span>
              </div>
            )}
          </div>
          {/* Profissional */}
          <div className="flex items-center gap-1.5 text-xs">
            <Users className="w-3 h-3 text-muted-foreground shrink-0" />
            <span>{profName}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <span className={`rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${bookingOriginInfo.tone}`} title={bookingOriginHover}>{bookingOriginHover}</span>
          </div>
          {/* Horário */}
          <div className="flex items-center gap-1.5 text-xs">
            <Clock className="w-3 h-3 text-muted-foreground shrink-0" />
            <span>{formatTime(new Date(appt.startTime))} – {formatTime(new Date(appt.endTime))}</span>
          </div>
          {/* Valor */}
          {appt.totalPrice && (
            <div className="flex items-center gap-1.5 text-xs">
              <DollarSign className="w-3 h-3 text-muted-foreground shrink-0" />
              <span className="font-medium">R$ {Number(appt.totalPrice).toFixed(2)}</span>
            </div>
          )}
          {/* Status */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-secondary text-secondary-foreground">
              {statusLabel[appt.status] ?? appt.status}
            </span>
            {appt.paymentStatus && (
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                appt.paymentStatus === "paid" ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" :
                appt.paymentStatus === "pending" ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200" :
                "bg-secondary text-secondary-foreground"
              }`}>
                {paymentStatusLabel[appt.paymentStatus] ?? appt.paymentStatus}
              </span>
            )}
          </div>
          {appt.notes && (
            <p className="text-xs text-muted-foreground italic border-t border-border pt-1">{appt.notes}</p>
          )}
          <div className="flex gap-2 border-t border-border pt-2">
            <p className="text-[10px] text-muted-foreground flex-1">Duplo clique para editar</p>
            {onOpenCheckout && appt.paymentStatus !== "paid" && (
              <button
                className="text-[10px] text-green-600 font-medium hover:underline flex items-center gap-0.5"
                onClick={() => onOpenCheckout(appt.id)}
              >
                <DollarSign className="w-2.5 h-2.5" />Abrir Caixa
              </button>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
    </>
  );
}

// ─── Droppable Time Column ────────────────────────────────────────────────────
function DroppableColumn({
  id,
  children,
  isOver,
  onSlotTap,
  startHour,
  slotHeight = SLOT_HEIGHT,
}: {
  id: string;
  children: React.ReactNode;
  isOver?: boolean;
  onSlotTap?: (hour: number, minute: number) => void;
  startHour?: number;
  slotHeight?: number;
}) {
  const { setNodeRef, isOver: dndIsOver } = useDroppable({ id });

  function handleClick(e: React.MouseEvent<HTMLDivElement>) {
    // Só ativa no mobile (touch) e se o alvo for o próprio container (não um card)
    if (!onSlotTap) return;
    const target = e.target as HTMLElement;
    // Ignora cliques em cards de agendamento, botões, etc.
    if (target.closest('[data-appt-card]') || target.closest('button') || target.closest('[data-block]')) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const totalMinutes = (y / slotHeight) * 60;
    const h = Math.floor(totalMinutes / 60) + (startHour ?? 7);
    const m = Math.round((totalMinutes % 60) / 15) * 15;
    const clampedH = Math.max(startHour ?? 7, Math.min(h, 23));
    const clampedM = m >= 60 ? 45 : m;
    onSlotTap(clampedH, clampedM);
  }

  return (
    <div
      ref={setNodeRef}
      className={`relative transition-colors ${dndIsOver || isOver ? "bg-primary/5" : ""}`}
      style={{ height: "100%", minHeight: slotHeight * 14 }}
      onClick={onSlotTap ? handleClick : undefined}
    >
      {children}
    </div>
  );
}

export default function AdminWeeklyCalendar() {
  const utils = trpc.useUtils();
  const [, navigate] = useLocation();
  const isMobile = useIsMobile();
  const [currentWeek, setCurrentWeek] = useState(() => new Date());
  const [agendaClock, setAgendaClock] = useState(() => new Date());
  // A grade semanal com sete colunas é útil no computador, mas reduz cartões e
  // textos demais em telefones. No celular, abrir diretamente no dia atual;
  // a visão semanal permanece disponível somente em telas a partir de tablet.
  const [viewMode, setViewMode] = useState<"week" | "staff" | "day">(() => isMobile ? "day" : "week");
  useEffect(() => {
    // O breakpoint pode ser confirmado depois da primeira renderização ou de
    // uma atualização em tempo real. No telefone a Semana não é oferecida,
    // então nunca manter a grade comprimida como estado ativo.
    if (isMobile && viewMode === "week") setViewMode("day");
  }, [isMobile, viewMode]);
  const [agendaDensity, setAgendaDensity] = useState<AgendaDensity>(() => {
    const saved = localStorage.getItem(AGENDA_DENSITY_KEY);
    return saved === "compact" || saved === "detailed" || saved === "standard" ? saved : "standard";
  });
  const [selectedProfId, setSelectedProfId] = useState<number | "all">("all");
  const [selectedBranchId, setSelectedBranchId] = useState<number | "all">("all");
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [blockForm, setBlockForm] = useState({ professionalIds: [] as string[], startDate: "", endDate: "", startTime: "", endTime: "", reason: "" });

  useEffect(() => {
    const interval = window.setInterval(() => setAgendaClock(new Date()), 30_000);
    return () => window.clearInterval(interval);
  }, []);

  // Bottom sheet de seleção de cliente no modal de novo agendamento
  const [clientSheetOpen, setClientSheetOpen] = useState(false);
  const [clientSheetSearch, setClientSheetSearch] = useState("");
  const clientSheetInputRef = useRef<HTMLInputElement>(null);

  // Bottom sheet de seleção de serviço no modal de novo agendamento
  const [serviceSheetOpen, setServiceSheetOpen] = useState(false);
  const [serviceSheetSearch, setServiceSheetSearch] = useState("");

  // Drag state
  const [draggingAppt, setDraggingAppt] = useState<any>(null);
  const [confirmMove, setConfirmMove] = useState<{
    apptId: number;
    newStartTime: Date;
    newEndTime: Date;
    newProfessionalId?: number;
    fromLabel: string;
    toLabel: string;
  } | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const weekDates = useMemo(() => getWeekDates(currentWeek), [currentWeek]);
  const slotHeight = AGENDA_DENSITIES[agendaDensity].slotHeight;
  const weekStart = weekDates[0]!;
  const weekEnd = useMemo(() => {
    const end = new Date(weekDates[6]!);
    end.setHours(23, 59, 59, 999);
    return end;
  }, [weekDates]);

  // Stable query inputs — object literals created outside useMemo cause infinite re-fetches
  const apptQueryInput = useMemo(
    () => ({ startDate: weekStart, endDate: weekEnd }),
    [weekStart, weekEnd]
  );
  const blocksQueryInput = useMemo(
    () => ({ dateFrom: weekStart, dateTo: weekEnd }),
    [weekStart, weekEnd]
  );
  const tanningArrivalIntentQueryInput = useMemo(
    () => ({ startDate: brasiliaDateString(weekStart), endDate: brasiliaDateString(weekEnd) }),
    [weekStart, weekEnd],
  );

  const { data: allProfessionals = [] } = (trpc as any).professionals.listForSchedule.useQuery();
  const { data: branches = [] } = (trpc as any).branches.listWithProfessionals.useQuery();
  const { data: services = [] } = trpc.services.listAdmin.useQuery();
  const { data: serviceProfessionalsMap } = (trpc as any).services.getAllProfessionalsMap.useQuery();
  const { data: mySalon } = trpc.salon.getMySalon.useQuery();
  // Filtra profissionais pela filial selecionada (apenas profissionais que executam serviços)
  const professionals = useMemo(() => {
    if (selectedBranchId === "all") return allProfessionals as any[];
    // Busca profissionais vinculados à filial selecionada via branch_professionals
    const branch = (branches as any[]).find((b: any) => b.id === selectedBranchId);
    if (!branch) return allProfessionals as any[];
    const linkedIds: number[] = branch.professionalIds ?? [];
    if (linkedIds.length === 0) return [];
    return (allProfessionals as any[]).filter((p: any) => linkedIds.includes(Number(p.id)));
  }, [allProfessionals, branches, selectedBranchId]);
  const isProfessionalEligibleForService = (professionalId: string | number | null | undefined, serviceId: string | number | null | undefined) => {
    if (!professionalId || !serviceId) return true;
    // Não mostrar combinações sem confirmação: antes da leitura do mapa, ou
    // caso ela falhe, a agenda permanece fechada para vínculos desconhecidos.
    if (!serviceProfessionalsMap) return false;
    return (serviceProfessionalsMap[Number(serviceId)] ?? []).includes(Number(professionalId));
  };
  const eligibleServicesForProfessional = (professionalId: string | number | null | undefined) => {
    if (!professionalId) return services as any[];
    return (services as any[]).filter((service: any) => isProfessionalEligibleForService(professionalId, service.id));
  };
  const eligibleProfessionalsForService = (serviceId: string | number | null | undefined) => {
    if (!serviceId) return professionals as any[];
    return (professionals as any[]).filter((professional: any) => isProfessionalEligibleForService(professional.id, serviceId));
  };
  const { data: appointments = [], refetch: refetchApptsQuery } = trpc.appointments.getByDateRange.useQuery(apptQueryInput);
  const { data: blocks = [], refetch: refetchBlocksQuery } = trpc.scheduleBlocks.list.useQuery(blocksQueryInput);
  // Forçar refetch imediato (sem depender de staleTime) para garantir que o card atualiza após edição
  function refetchAppts() {
    utils.appointments.getByDateRange.invalidate();
    refetchApptsQuery();
  }
  function refetchBlocks() {
    utils.scheduleBlocks.list.invalidate();
    refetchBlocksQuery();
  }
  // Horário de funcionamento do salão (grade dinâmica)
  const { data: salonSchedule = [] } = (trpc as any).salon.getWorkingSchedule.useQuery();
  // Horários de atendimento de todos os profissionais
  const { data: allProfWorkingHours = [] } = (trpc as any).professionals.getAllWorkingHours.useQuery();
  // Contexto do funcionário logado (para pré-selecionar e restringir profissional)
  const { data: staffCtx } = (trpc as any).staff.myContext.useQuery();
  const currentStaffRole = staffCtx?.staffRole ?? staffCtx?.teamRole;
  const canViewTanningArrivalIntents = Number(mySalon?.id) === 60002
    && (mySalon as any)?.tanningArrivalIntentsEnabled === true
    && ["admin", "recepcionista", "caixa"].includes(currentStaffRole ?? "");
  const { data: tanningArrivalIntents = [], isLoading: loadingTanningArrivalIntents } = (trpc as any).queue.listTanningArrivalIntents.useQuery(
    tanningArrivalIntentQueryInput,
    { enabled: canViewTanningArrivalIntents, refetchInterval: 30_000, refetchIntervalInBackground: true },
  );
  const isLinkedProfessional = ["funcionario", "professional", "profissional"].includes(
    staffCtx?.staffRole ?? staffCtx?.teamRole ?? ""
  );
  const linkedProfId: number | null = isLinkedProfessional && staffCtx?.professionalId ? Number(staffCtx.professionalId) : null;
  // Pré-seleciona o profissional vinculado quando os dados chegam (apenas uma vez)
  const didPreselect = useRef(false);
  useEffect(() => {
    if (linkedProfId && !didPreselect.current) {
      setSelectedProfId(linkedProfId);
      didPreselect.current = true;
    }
  }, [linkedProfId]);

  // Deriva startHour/endHour dinâmicos a partir do horário de funcionamento do salão
  const dynamicHours = useMemo(() => {
    if (salonSchedule.length === 0) return Array.from({ length: 14 }, (_, i) => i + 7); // fallback 7h-20h
    const openDays = salonSchedule.filter((d: any) => d.isOpen && d.openTime && d.closeTime);
    if (openDays.length === 0) return Array.from({ length: 14 }, (_, i) => i + 7);
    const minHour = Math.min(...openDays.map((d: any) => parseInt(d.openTime.split(":")[0])));
    const maxHour = Math.max(...openDays.map((d: any) => parseInt(d.closeTime.split(":")[0]) + 1));
    return Array.from({ length: maxHour - minHour }, (_, i) => i + minHour);
  }, [salonSchedule]);

  // Verifica se um dia da semana está aberto (0=Dom, 1=Seg ... 6=Sáb)
  function isDayOpen(jsDay: number): boolean {
    if (salonSchedule.length === 0) return true; // sem configuração = todos abertos
    const entry = salonSchedule.find((d: any) => d.dayOfWeek === jsDay);
    return entry ? entry.isOpen : true;
  }

  // Retorna o intervalo de horas abertas para um dia específico
  function getDayHourRange(jsDay: number): { open: number; close: number } | null {
    if (salonSchedule.length === 0) return null;
    const entry = salonSchedule.find((d: any) => d.dayOfWeek === jsDay && d.isOpen);
    if (!entry || !entry.openTime || !entry.closeTime) return null;
    return {
      open: parseInt(entry.openTime.split(":")[0]),
      close: parseInt(entry.closeTime.split(":")[0]),
    };
  }

  // Verifica se um profissional atende em um determinado dia e hora
  function isProfAvailable(profId: number, jsDay: number, hour: number): boolean {
    const profHours = allProfWorkingHours.filter((h: any) => h.professionalId === profId);
    if (profHours.length === 0) return true; // sem configuração = sempre disponível
    const dayEntry = profHours.find((h: any) => h.dayOfWeek === jsDay);
    if (!dayEntry) return false; // dia não configurado = não atende
    const startH = parseInt(dayEntry.startTime.split(":")[0]);
    const endH = parseInt(dayEntry.endTime.split(":")[0]);
    return hour >= startH && hour < endH;
  }

  const moveAppt = trpc.appointments.move.useMutation({
    onSuccess: () => {
      toast.success("Agendamento movido com sucesso");
      setConfirmMove(null);
      refetchAppts();
    },
    onError: (e: any) => {
      toast.error(e.message);
      setConfirmMove(null);
    },
  });

  const registerAgendaArrival = (trpc as any).queue.registerArrival.useMutation({
    onSuccess: (result: { alreadyQueued?: boolean; position?: number }) => {
      if (result.alreadyQueued) {
        toast.info(`A cliente já está na fila na posição ${result.position ?? "atual"}.`);
      } else {
        toast.success(`Chegada registrada. Cliente na posição ${result.position}.`);
      }
      utils.queue.list.invalidate();
    },
    onError: (error: { message: string }) => toast.error(error.message),
  });

  const handleAgendaArrival = (appointmentId: number) => {
    registerAgendaArrival.mutate({ appointmentId });
  };

  const updateAgendaStatus = trpc.appointments.updateStatus.useMutation({
    onSuccess: () => {
      toast.success("Status do atendimento atualizado.");
      refetchAppts();
      utils.appointments.getByDateRange.invalidate();
    },
    onError: (error: { message: string }) => toast.error(error.message),
  });

  const handleAgendaStatusUpdate = (appointmentId: number, status: "confirmed" | "in_service" | "completed" | "no_show") => {
    updateAgendaStatus.mutate({ id: appointmentId, status });
  };

  const createBlock = (trpc as any).scheduleBlocks.createMany.useMutation({
    onSuccess: (result: { createdCount: number }) => {
      toast.success(`${result.createdCount} ${result.createdCount === 1 ? "bloqueio criado" : "bloqueios criados"} com sucesso`);
      setBlockModalOpen(false);
      setBlockForm({ professionalIds: [], startDate: "", endDate: "", startTime: "", endTime: "", reason: "" });
      refetchBlocks();
    },
    onError: (e: any) => toast.error(e.message),
  });
  const deleteBlock = trpc.scheduleBlocks.delete.useMutation({
    onSuccess: () => { toast.success("Bloqueio removido"); refetchBlocks(); },
    onError: (e: any) => toast.error(e.message),
  });

  // ─── Validação de conflito de horário ────────────────────────────────────────────────
  const [conflictWarning, setConflictWarning] = useState<string | null>(null);
  const [conflictChecked, setConflictChecked] = useState(false);

  // ─── Modal de edição de agendamento (duplo clique) ───────────────────────────────────
  const [editAppt, setEditAppt] = useState<any | null>(null);
  // No celular, o toque na lista diária prioriza as ações operacionais; o
  // formulário completo continua disponível pelo botão de detalhes do painel.
  const [mobileAgendaQuickActionAppt, setMobileAgendaQuickActionAppt] = useState<any | null>(null);
  const [notifyClient, setNotifyClient] = useState(true);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [confirmCancelRequest, setConfirmCancelRequest] = useState(false);
  // Verifica se o usuário logado é recepcionista (não pode cancelar diretamente)
  const isReceptionist = staffCtx?.teamRole === "receptionist" || staffCtx?.staffRole === "receptionist";
  // Serviços extras do agendamento em edição
  const [extraServices, setExtraServices] = useState<Array<{ serviceId: string; price: string; durationMinutes: number; professionalId?: string }>>([]); 
  // Flag para evitar que background refetch sobrescreva as edições do usuário
  const servicesLoadedRef = useRef(false);
  // Produtos vendidos no agendamento
  const [apptProducts, setApptProducts] = useState<Array<{ productId: number; productName: string; quantity: number; unitPrice: number }>>([]);
  const [selectedProductId, setSelectedProductId] = useState<string>("");
  const productsQueryInput = useMemo(() => ({ search: "", category: "" }), []);
  const { data: allProductsList = [] } = trpc.products.list.useQuery(productsQueryInput, { enabled: !!editAppt });
  const updateApptProductsMutation = (trpc as any).appointments.updateProducts.useMutation({
    onSuccess: () => { toast.success("Produtos salvos!"); },
    onError: (e: any) => { toast.error("Erro ao salvar produtos: " + e.message); },
  });
  // Confirmação de exclusão de serviço adicional (idx do item pendente de exclusão)
  const [confirmRemoveServiceIdx, setConfirmRemoveServiceIdx] = useState<number | null>(null);
  const updateServicesMutation = (trpc as any).appointments.updateServices.useMutation({
    onSuccess: () => { refetchAppts(); },
    onError: (e: any) => console.error("[updateServices]", e.message),
  });
  const [editForm, setEditForm] = useState({
    clientName: "",
    clientPhone: "",
    clientEmail: "",
    professionalId: "",
    serviceId: "",
    startDate: "",
    startTime: "",
    endTime: "",
    status: "pending" as string,
    notes: "",
    totalPrice: "",
    paymentStatus: "unpaid" as string,
    paymentMethod: "" as string,
  });
  const editServiceOptions = eligibleServicesForProfessional(editForm.professionalId);
  const editProfessionalOptions = eligibleProfessionalsForService(editForm.serviceId);

  // ─── PIN para desconto no modal de edição ────────────────────────────────────
  const [editPinOpen, setEditPinOpen] = useState(false);
  const [editPinApproved, setEditPinApproved] = useState(false);
  const [userEditedPrice, setUserEditedPrice] = useState(false);
  const editVerifyPin = (trpc as any).authorization.verifyPin.useMutation();

  // ─── Autocomplete de clientes no modal de Novo Agendamento ─────────────────
  const [clientSearchQuery, setClientSearchQuery] = useState("");
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [selectedClientPhoto, setSelectedClientPhoto] = useState<string | null>(null);
  const clientSearchRef = useRef<HTMLDivElement>(null);

  // Autocomplete por telefone
  const [phoneSearchQuery, setPhoneSearchQuery] = useState("");
  const [showPhoneSuggestions, setShowPhoneSuggestions] = useState(false);
  const phoneSearchRef = useRef<HTMLDivElement>(null);
  const weekGridScrollRef = useRef<HTMLDivElement>(null);
  const weekHeaderScrollRef = useRef<HTMLDivElement>(null);
  const [scrollToTodayTrigger, setScrollToTodayTrigger] = useState(0);

  const { data: clientSuggestionsData } = (trpc as any).clients.listDirect.useQuery(
    { search: clientSearchQuery, page: 1, pageSize: 8 },
    { enabled: clientSearchQuery.length >= 2 }
  );
  const clientSuggestions: any[] = clientSuggestionsData?.clients ?? [];

  const { data: phoneSuggestionsData } = (trpc as any).clients.listDirect.useQuery(
    { search: phoneSearchQuery, page: 1, pageSize: 8 },
    { enabled: phoneSearchQuery.length >= 3 }
  );
  const phoneSuggestions: any[] = (phoneSuggestionsData?.clients ?? []).filter(
    (c: any) => c.phone && c.phone.replace(/\D/g, "").includes(phoneSearchQuery.replace(/\D/g, ""))
  );

  // Query para o bottom sheet de busca de cliente (mobile)
  const debouncedClientSheetSearch = clientSheetSearch;
  const { data: clientSheetSuggestionsData } = (trpc as any).clients.listDirect.useQuery(
    { search: debouncedClientSheetSearch, page: 1, pageSize: 20 },
    { enabled: debouncedClientSheetSearch.length >= 2 }
  );
  // Sobrescreve clientSuggestionsData para o sheet usar os mesmos dados
  const sheetClientResults = clientSheetSuggestionsData ?? clientSuggestionsData;

  // Fechar dropdowns ao clicar fora
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (clientSearchRef.current && !clientSearchRef.current.contains(e.target as Node)) {
        setShowClientSuggestions(false);
      }
      if (phoneSearchRef.current && !phoneSearchRef.current.contains(e.target as Node)) {
        setShowPhoneSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Scroll para o dia de hoje na grade semanal
  useEffect(() => {
    if (scrollToTodayTrigger === 0) return;
    const today = new Date();
    const todayIdx = weekDates.findIndex((d) => d.toDateString() === today.toDateString());
    if (todayIdx < 0) return; // hoje não está na semana visível
    // Calcula a posição X da coluna de hoje
    const timeColWidth = isMobile ? 44 : 56;
    const containerWidth = weekGridScrollRef.current?.clientWidth ?? 0;
    let scrollLeft = 0;
    if (isMobile) {
      // No mobile cada coluna tem 80px
      scrollLeft = timeColWidth + todayIdx * 80 - (containerWidth / 2 - 40);
    } else {
      // No desktop as colunas têm largura igual
      const colWidth = (containerWidth - timeColWidth) / 7;
      scrollLeft = timeColWidth + todayIdx * colWidth - (containerWidth / 2 - colWidth / 2);
    }
    scrollLeft = Math.max(0, scrollLeft);
    weekGridScrollRef.current?.scrollTo({ left: scrollLeft, behavior: "smooth" });
    weekHeaderScrollRef.current?.scrollTo({ left: scrollLeft, behavior: "smooth" });
  }, [scrollToTodayTrigger]);

  function selectClientSuggestion(client: any) {
    setNewForm((f) => ({
      ...f,
      clientName: client.name ?? "",
      clientPhone: client.phone ?? "",
      clientEmail: client.email ?? "",
    }));
    setClientSearchQuery(client.name ?? "");
    setPhoneSearchQuery(client.phone ?? "");
    setSelectedClientPhoto(client.photoUrl ?? null);
    setShowClientSuggestions(false);
    setShowPhoneSuggestions(false);
  }
  // ─── Quick-Add: toque em slot vazio no mobile ─────────────────────────────────────────────
  const [quickAddSlot, setQuickAddSlot] = useState<{ date: string; hour: number; minute: number; profId?: number } | null>(null);

  function openQuickAdd(date: string, hour: number, minute: number, profId?: number) {
    if (!isMobile) return; // apenas mobile
    setQuickAddSlot({ date, hour, minute, profId });
  }

  function confirmQuickAdd() {
    if (!quickAddSlot) return;
    const { date, hour, minute } = quickAddSlot;
    const startTime = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
    const endH = minute + 60 >= 60 ? hour + 1 : hour;
    const endM = (minute + 60) % 60;
    const endTime = `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
    setNewForm({
      clientName: '',
      clientPhone: '',
      clientEmail: '',
      professionalId: linkedProfId ? String(linkedProfId) : '',
      serviceId: '',
      date,
      startTime,
      endTime,
      notes: '',
      totalPrice: '',
    });
    setNewExtraServices([]);
    setClientSearchQuery('');
    setSelectedClientPhoto(null);
    setShowClientSuggestions(false);
    setQuickAddSlot(null);
    setNewApptOpen(true);
  }

  // ─── Modal de Novo Agendamento ────────────────────────────────────────────────────
  const [newApptOpen, setNewApptOpen] = useState(false);
  const [newForm, setNewForm] = useState({
    clientName: "",
    clientPhone: "",
    clientEmail: "",
    professionalId: "",
    serviceId: "",
    date: new Date().toISOString().split("T")[0] as string,
    startTime: "09:00",
    endTime: "10:00",
    notes: "",
    totalPrice: "",
  });

  // Quando o profissional abre o próprio formulário, a agenda dele é sempre o destino.
  useEffect(() => {
    if (!newApptOpen || !linkedProfId) return;
    setNewForm((current) => current.professionalId === String(linkedProfId)
      ? current
      : { ...current, professionalId: String(linkedProfId) });
  }, [newApptOpen, linkedProfId]);

  function openNewApptModal() {
    setArrivalIntentIdForNewAppt(null);
    setNewForm({
      clientName: "",
      clientPhone: "",
      clientEmail: "",
      // Para equipe administrativa, o profissional é escolhido somente após o serviço.
      // O profissional vinculado continua restrito à própria agenda.
      professionalId: linkedProfId ? String(linkedProfId) : "",
      serviceId: "",
      date: brasiliaDateString(),
      startTime: "09:00",
      endTime: "10:00",
      notes: "",
      totalPrice: "",
    });
    setNewExtraServices([]);
    setClientSearchQuery("");
    setSelectedClientPhoto(null);
    setShowClientSuggestions(false);
    setNewApptOpen(true);
  }

  function openArrivalIntentAsNewAppointment(intent: any) {
    setArrivalIntentIdForNewAppt(Number(intent.id));
    let serviceIds: number[] = [];
    try {
      const parsed = JSON.parse(intent.serviceIds ?? "[]");
      serviceIds = Array.isArray(parsed)
        ? parsed.map(Number).filter((id) => Number.isFinite(id) && id > 0)
        : [];
    } catch {
      serviceIds = [];
    }

    const primaryServiceId = serviceIds.find((id) => services.some((service: any) => Number(service.id) === id));
    const startTime = intent.preferredTime
      || (intent.preferredPeriod === "tarde" ? "14:00" : intent.preferredPeriod === "noite" ? "18:00" : "09:00");
    const primaryService = services.find((service: any) => Number(service.id) === primaryServiceId);
    const [hours, minutes] = startTime.split(":").map(Number);
    const endAt = new Date(2000, 0, 1, Number.isFinite(hours) ? hours : 9, Number.isFinite(minutes) ? minutes : 0);
    endAt.setMinutes(endAt.getMinutes() + Number(primaryService?.durationMinutes ?? 60));
    const endTime = `${String(endAt.getHours()).padStart(2, "0")}:${String(endAt.getMinutes()).padStart(2, "0")}`;

    setNewForm({
      clientName: intent.clientName ?? "",
      clientPhone: intent.clientPhone ?? "",
      clientEmail: "",
      professionalId: linkedProfId ? String(linkedProfId) : "",
      serviceId: primaryServiceId ? String(primaryServiceId) : "",
      date: intent.preferredDate ?? brasiliaDateString(),
      startTime,
      endTime,
      notes: `Origem: intenção de chegada de bronze #${intent.id}. Confirme profissional, horário e salvamento antes de criar o agendamento.`,
      totalPrice: primaryService?.price ? String(primaryService.price) : "",
    });
    setNewExtraServices(
      serviceIds
        .filter((id) => id !== primaryServiceId)
        .map((id) => services.find((service: any) => Number(service.id) === id))
        .filter(Boolean)
        .map((service: any) => ({ serviceId: String(service.id), price: String(service.price ?? ""), durationMinutes: Number(service.durationMinutes ?? 60) })),
    );
    setClientSearchQuery(intent.clientName ?? "");
    setPhoneSearchQuery(intent.clientPhone ?? "");
    setSelectedClientPhoto(null);
    setShowClientSuggestions(false);
    setShowPhoneSuggestions(false);
    setNewApptOpen(true);
  }

  // Serviços extras para o modal de Novo Agendamento
  const [newExtraServices, setNewExtraServices] = useState<Array<{ serviceId: string; price: string; durationMinutes: number; professionalId?: string }>>([]);
  const [arrivalIntentIdForNewAppt, setArrivalIntentIdForNewAppt] = useState<number | null>(null);
  const newAppointmentProfessionalId = linkedProfId ?? newForm.professionalId;
  const newServiceOptions = eligibleServicesForProfessional(newAppointmentProfessionalId);
  const newProfessionalOptions = eligibleProfessionalsForService(newForm.serviceId);

  const createApptMutation = (trpc as any).walkIn.create.useMutation({
    onSuccess: async (data: any) => {
      toast.success("Agendamento criado! Você pode adicionar fotos agora.");
      setNewApptOpen(false);
      setNewExtraServices([]);
      setArrivalIntentIdForNewAppt(null);
      refetchAppts();
      utils.queue.listTanningArrivalIntents.invalidate();
      // Abrir modal de edição para permitir adicionar fotos imediatamente
      if (data?.appointmentId) {
        setTimeout(async () => {
          try {
            const res = await fetch(`/api/trpc/appointments.getById?input=${encodeURIComponent(JSON.stringify({ json: { id: data.appointmentId } }))}`, { credentials: "include" });
            if (res.ok) {
              const json = await res.json();
              const appt = json?.result?.data?.json ?? json?.result?.data;
              if (appt) openEditModal(appt);
            }
          } catch (_) { /* não bloquear */ }
        }, 500);
      }
    },
    onError: (e: any) => toast.error(e.message),
  });

  // Verificar conflitos via API
  const checkConflictMutation = (trpc as any).appointments.checkConflict.useQuery;

  async function doCheckConflicts(params: { professionalIds: number[]; startTime: Date; endTime: Date; excludeId?: number }): Promise<string | null> {
    try {
      // Usar fetch direto para verificar conflito (query síncrona)
      const res = await fetch(`/api/trpc/appointments.checkConflict?input=${encodeURIComponent(JSON.stringify({
        professionalIds: params.professionalIds,
        startTime: params.startTime,
        endTime: params.endTime,
        excludeAppointmentId: params.excludeId,
      }))}`, { credentials: "include" });
      if (!res.ok) return null;
      const json = await res.json();
      const conflicts = json?.result?.data?.conflicts ?? [];
      if (conflicts.length === 0) return null;
      const names = Array.from(new Set(conflicts.map((c: any) => c.clientName))).slice(0, 3).join(", ");
      return `Conflito de horário: ${conflicts.length} agendamento(s) já existem neste período (${names})`;
    } catch { return null; }
  }

  // Calcula o horário de fim somando duração do serviço principal + todos os adicionais
  function calcEndTimeFromServices(startTime: string, primaryServiceId: string, extraSvcs: typeof newExtraServices): string {
    const primarySvc = services.find((s: any) => String(s.id) === primaryServiceId);
    const primaryDur = primarySvc?.durationMinutes ?? 60;
    const extraDur = extraSvcs.filter(es => es.serviceId).reduce((sum, es) => {
      const svc = services.find((s: any) => String(s.id) === es.serviceId);
      return sum + (svc?.durationMinutes ?? es.durationMinutes ?? 60);
    }, 0);
    const totalDur = primaryDur + extraDur;
    const [h, m] = startTime.split(":").map(Number);
    const startMins = h * 60 + m;
    const endMins = startMins + totalDur;
    const endH = Math.floor(endMins / 60) % 24;
    const endM = endMins % 60;
    return `${String(endH).padStart(2, "0")}:${String(endM).padStart(2, "0")}`;
  }

  async function handleCreateAppt() {
    if (!newForm.clientName.trim()) { toast.error("Informe o nome do cliente"); return; }
    if (!newForm.clientPhone.trim()) { toast.error("Informe o telefone do cliente"); return; }
    const effectiveProfessionalId = linkedProfId ? String(linkedProfId) : newForm.professionalId;
    if (!effectiveProfessionalId) { toast.error("Selecione o profissional"); return; }
    if (!newForm.serviceId) { toast.error("Selecione o serviço"); return; }
    if (!newForm.date || !newForm.startTime) { toast.error("Informe data e horário"); return; }
    if (newExtraServices.some((service) => !service.serviceId)) {
      toast.error("Selecione ou remova cada serviço adicional antes de salvar.");
      return;
    }
    const extraServiceIds = newExtraServices.map((service) => Number(service.serviceId));
    if (extraServiceIds.includes(Number(newForm.serviceId)) || new Set(extraServiceIds).size !== extraServiceIds.length) {
      toast.error("Cada serviço pode ser incluído apenas uma vez no agendamento.");
      return;
    }
    const extraServices = newExtraServices.map((extra) => {
      const service = services.find((item: any) => String(item.id) === extra.serviceId);
      const enteredPrice = extra.price.trim() === "" ? Number(service?.price ?? 0) : Number(extra.price);
      return {
        serviceId: Number(extra.serviceId),
        price: enteredPrice,
        professionalId: extra.professionalId && extra.professionalId !== "none" ? Number(extra.professionalId) : null,
      };
    });
    if (extraServices.some((service) => !Number.isFinite(service.price) || service.price < 0)) {
      toast.error("Informe um valor válido para cada serviço adicional.");
      return;
    }
    const startTime = fromBrasiliaDateTime(newForm.date, newForm.startTime);
    const endTime = fromBrasiliaDateTime(newForm.date, newForm.endTime || newForm.startTime);
    // Coletar todos os profissionais envolvidos
    const profIds = [Number(effectiveProfessionalId)];
    newExtraServices.forEach(es => { if (es.professionalId && es.professionalId !== "none") profIds.push(Number(es.professionalId)); });
    const uniqueProfIds = Array.from(new Set(profIds.filter(Boolean)));
    // Verificar conflito (se já foi verificado e confirmado, pular)
    if (!conflictChecked) {
      const warning = await doCheckConflicts({ professionalIds: uniqueProfIds, startTime, endTime });
      if (warning) {
        setConflictWarning(warning);
        setConflictChecked(true);
        return; // Aguardar confirmação do usuário
      }
    }
    setConflictWarning(null);
    setConflictChecked(false);
    createApptMutation.mutate({
      clientName: newForm.clientName.trim(),
      clientPhone: newForm.clientPhone || undefined,
      clientEmail: newForm.clientEmail || undefined,
      professionalId: Number(effectiveProfessionalId),
      serviceId: Number(newForm.serviceId),
      extraServices,
      startTime,
      notes: newForm.notes || undefined,
      arrivalIntentId: arrivalIntentIdForNewAppt ?? undefined,
    });
  }

  const cancelApptMutation = trpc.appointments.cancel.useMutation({
    onSuccess: () => {
      toast.success("Agendamento cancelado!");
      setEditAppt(null);
      setConfirmCancel(false);
      refetchAppts();
    },
    onError: (e: any) => toast.error(e.message),
  });
  const requestCancelMutation = (trpc as any).cancellationRequests.create.useMutation({
    onSuccess: () => {
      toast.success("Solicitação de cancelamento enviada! Aguardando aprovação do administrador.");
      setEditAppt(null);
      setConfirmCancelRequest(false);
      setCancelReason("");
      refetchAppts();
    },
    onError: (e: any) => toast.error(e.message ?? "Erro ao enviar solicitação."),
  });

  // Buscar produtos vendidos ao abrir o modal
  const { data: apptProductsData } = (trpc as any).appointments.getProducts.useQuery(
    { appointmentId: editAppt?.id ?? 0 },
    { enabled: !!editAppt?.id }
  );
  useEffect(() => {
    if (!apptProductsData) return;
    setApptProducts((apptProductsData as any[]).map((p: any) => ({
      productId: p.productId,
      productName: p.productName,
      quantity: Number(p.quantity),
      unitPrice: Number(p.unitPrice),
    })));
  }, [apptProductsData]);
  // Limpar produtos ao fechar o modal
  useEffect(() => {
    if (!editAppt) { setApptProducts([]); setSelectedProductId(""); }
  }, [editAppt]);
  // Buscar serviços extras ao abrir o modal
  const { data: apptServicesData, refetch: refetchApptServices } = (trpc as any).appointments.getServices.useQuery(
    { appointmentId: editAppt?.id ?? 0 },
    { enabled: !!editAppt?.id }
  );
  // Sincronizar serviços extras apenas na primeira carga (evita sobrescrita por background refetch)
  useEffect(() => {
    if (!apptServicesData || servicesLoadedRef.current) return;
    // Filtrar o serviço principal para não aparecer como extra duplicado
    const primarySvcId = editAppt?.serviceId ? String(editAppt.serviceId) : null;
    const extras = (apptServicesData as any[])
      .filter((row: any) => !primarySvcId || String(row.serviceId) !== primarySvcId)
      .map((row: any) => ({
        serviceId: String(row.serviceId),
        price: String(row.price),
        durationMinutes: row.durationMinutes ?? 60,
        professionalId: row.professionalId ? String(row.professionalId) : undefined,
      }));
    setExtraServices(extras);
    servicesLoadedRef.current = true; // marca como carregado — não sobrescreve mais
  }, [apptServicesData, editAppt?.serviceId]);

  // Recalcular totalPrice automaticamente quando serviço principal ou adicionais mudam
  // Mas NÃO sobrescreve se o usuário editou manualmente o valor
  useEffect(() => {
    if (!editAppt) return; // só recalcula quando o modal está aberto
    if (userEditedPrice) return; // usuário editou manualmente — não sobrescrever
    const primarySvc = services.find((s: any) => String(s.id) === editForm.serviceId);
    const primaryPrice = primarySvc ? Number(primarySvc.price ?? 0) : 0;
    const extrasTotal = extraServices
      .filter(es => es.serviceId)
      .reduce((sum, es) => {
        const svc = services.find((s: any) => String(s.id) === es.serviceId);
        return sum + Number(es.price || svc?.price || 0);
      }, 0);
    const newTotal = (primaryPrice + extrasTotal).toFixed(2);
    setEditForm(f => ({ ...f, totalPrice: newTotal }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraServices, editForm.serviceId, editAppt?.id, userEditedPrice]);
  function openEditModal(appt: any) {
    const start = new Date(appt.startTime);
    const end = new Date(appt.endTime);
    setEditAppt(appt);
    setConfirmCancel(false);
    setNotifyClient(true);
    setExtraServices([]); // Limpa enquanto carrega
    servicesLoadedRef.current = false; // Permite carregar os serviços do backend para este agendamento
    setUserEditedPrice(false); // Reseta flag de edição manual de preço
    setEditPinApproved(false); // Reseta aprovação de PIN
    setEditForm({
      clientName: appt.clientName ?? "",
      clientPhone: appt.clientPhone ?? "",
      clientEmail: appt.clientEmail ?? "",
      professionalId: String(appt.professionalId ?? ""),
      serviceId: String(appt.serviceId ?? ""),
      startDate: start.toISOString().split("T")[0]!,
      startTime: start.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }),
      endTime: end.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }),
      status: appt.status ?? "pending",
      notes: appt.notes ?? "",
      totalPrice: appt.totalPrice ? String(appt.totalPrice) : "",
      paymentStatus: appt.paymentStatus ?? "unpaid",
      paymentMethod: appt.paymentMethod ?? "",
    });
  }

  const editApptMutation = (trpc as any).appointments.editFull.useMutation({
    onSuccess: async () => {
      // Refetch primeiro para garantir que os dados estão atualizados antes de fechar o modal
      await refetchApptsQuery();
      utils.appointments.getByDateRange.invalidate();
      toast.success("Agendamento atualizado com sucesso!");
      setEditAppt(null);
    },
    onError: (e: any) => toast.error(e.message),
  });

  async function handleSaveEdit() {
    if (!editAppt) return;
    const [hStart, mStart] = editForm.startTime.split(":").map(Number);
    const [hEnd, mEnd] = editForm.endTime.split(":").map(Number);
    const newStart = new Date(`${editForm.startDate}T00:00:00`);
    newStart.setHours(hStart!, mStart!, 0, 0);
    const newEnd = new Date(`${editForm.startDate}T00:00:00`);
    newEnd.setHours(hEnd!, mEnd!, 0, 0);
    if (newEnd <= newStart) { toast.error("Horário de fim deve ser após o início"); return; }
    // Verificar conflito de horário (se ainda não foi confirmado)
    if (!conflictChecked) {
      const profIds = editForm.professionalId ? [Number(editForm.professionalId)] : [];
      extraServices.forEach(es => { if (es.professionalId && es.professionalId !== "none") profIds.push(Number(es.professionalId)); });
      const uniqueProfIds = Array.from(new Set(profIds.filter(Boolean)));
      if (uniqueProfIds.length > 0) {
        const warning = await doCheckConflicts({ professionalIds: uniqueProfIds, startTime: newStart, endTime: newEnd, excludeId: editAppt.id });
        if (warning) {
          setConflictWarning(warning);
          setConflictChecked(true);
          return;
        }
      }
    }
    setConflictWarning(null);
    setConflictChecked(false);
    // Detectar se houve mudança de horário ou profissional para notificação
    const oldStart = new Date(editAppt.startTime);
    const timeChanged = newStart.getTime() !== oldStart.getTime();
    const profChanged = editForm.professionalId && Number(editForm.professionalId) !== editAppt.professionalId;
    const shouldNotify = notifyClient && (timeChanged || profChanged) && !!editForm.clientPhone;
    const oldProf = profChanged ? (editAppt.professionalName ?? undefined) : undefined;
    // Calcular totalPrice: se o usuário editou manualmente, usar o valor digitado
    // Caso contrário, recalcular com base nos serviços
    let finalTotalPrice = editForm.totalPrice;
    if (!userEditedPrice) {
      const primaryPrice = editForm.serviceId
        ? Number(services.find((s: any) => String(s.id) === editForm.serviceId)?.price ?? 0)
        : 0;
      const extrasTotal = extraServices
        .filter(es => es.serviceId)
        .reduce((sum, es) => {
          const svc = services.find((s: any) => String(s.id) === es.serviceId);
          return sum + Number(es.price || svc?.price || 0);
        }, 0);
      if (primaryPrice + extrasTotal > 0) finalTotalPrice = (primaryPrice + extrasTotal).toFixed(2);
    }
    // Primeiro atualizar serviços (await), depois salvar o agendamento — garante que o refetch veja dados atualizados
    try {
      const primarySvcId = editForm.serviceId ? Number(editForm.serviceId) : undefined;
      // Salvar APENAS os serviços extras na tabela appointment_services (NÃO incluir o serviço principal)
      const onlyExtras = extraServices
        .filter(es => es.serviceId && es.serviceId !== editForm.serviceId)
        .map(es => {
          const svc = services.find((s: any) => String(s.id) === es.serviceId);
          return { serviceId: Number(es.serviceId), price: es.price || String(svc?.price ?? 0), durationMinutes: svc?.durationMinutes ?? 60, professionalId: es.professionalId ? Number(es.professionalId) : null };
        });
      await updateServicesMutation.mutateAsync({
        appointmentId: editAppt.id,
        services: onlyExtras,
        primaryServiceId: primarySvcId,
        primaryProfessionalId: editForm.professionalId ? Number(editForm.professionalId) : undefined,
      });
    } catch (e) {
      console.error("[updateServices]", e);
    }
    editApptMutation.mutate({
      id: editAppt.id,
      clientName: editForm.clientName || undefined,
      clientPhone: editForm.clientPhone || undefined,
      clientEmail: editForm.clientEmail || undefined,
      professionalId: editForm.professionalId ? Number(editForm.professionalId) : undefined,
      serviceId: editForm.serviceId ? Number(editForm.serviceId) : undefined,
      startTime: newStart,
      endTime: newEnd,
      status: editForm.status as any,
      notes: editForm.notes || undefined,
      totalPrice: finalTotalPrice || undefined,
      paymentStatus: editForm.paymentStatus as any,
      paymentMethod: editForm.paymentMethod ? editForm.paymentMethod as any : null,
      notifyClient: shouldNotify,
      oldStartTime: shouldNotify ? oldStart : undefined,
      oldProfessionalName: oldProf,
      serviceName: editAppt.serviceName ?? editAppt.service?.name ?? undefined,
    });
  }

  const activeHours = dynamicHours.length > 0 ? dynamicHours : HOURS;
  const startHour = activeHours[0]!;

  // ─── Drag handlers ───────────────────────────────────────────────────────────
  function handleDragStart(event: DragStartEvent) {
    setDraggingAppt(event.active.data.current?.appt ?? null);
  }

  function handleDragEnd(event: DragEndEvent) {
    setDraggingAppt(null);
    const { active, over, delta } = event;
    if (!over || !active.data.current?.appt) return;

    const appt = active.data.current.appt;
    // droppable id format: "col-{dateStr}-{profId}" or "col-{dateStr}-{profId}-staff"
    const parts = String(over.id).replace(/^col-/, "").split("-");
    // dateStr is YYYY-MM-DD (3 parts), profId is last (may have trailing "-staff")
    const isStaff = String(over.id).endsWith("-staff");
    const cleanParts = isStaff ? parts.slice(0, -1) : parts; // remove "staff" suffix
    const profId = Number(cleanParts[cleanParts.length - 1]);
    const dateStr = cleanParts.slice(0, 3).join("-");

    const origStart = new Date(appt.startTime);
    const origEnd = new Date(appt.endTime);
    const duration = origEnd.getTime() - origStart.getTime();

    // ─── Calcular novo horário usando delta.y (deslocamento vertical em px) ───
    // delta.y é o deslocamento total do ponteiro desde o início do arraste
    const deltaHours = delta.y / slotHeight; // quantas horas o card foi deslocado
    // Arredondar para o slot de 15 minutos mais próximo
    const deltaMinutes = Math.round((deltaHours * 60) / 15) * 15;

    const newStart = new Date(origStart.getTime() + deltaMinutes * 60000);
    // Ajustar para a data da coluna de destino (caso mude de dia)
    const [y, mo, d] = dateStr.split("-").map(Number);
    newStart.setFullYear(y!, mo! - 1, d!);
    const newEnd = new Date(newStart.getTime() + duration);

    const isSameTime = newStart.getTime() === origStart.getTime();
    const isSameProf = profId === appt.professionalId;
    if (isSameTime && isSameProf) return;

    const profName = professionals.find((p: any) => p.id === profId)?.name ?? "Profissional";
    const fromLabel = `${formatTime(origStart)} – ${formatTime(origEnd)}`;
    const toLabel = `${newStart.toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "short" })} ${formatTime(newStart)} – ${formatTime(newEnd)} · ${profName}`;

    setConfirmMove({
      apptId: appt.id,
      newStartTime: newStart,
      newEndTime: newEnd,
      newProfessionalId: profId !== appt.professionalId ? profId : undefined,
      fromLabel,
      toLabel,
    });
  }

  function handleCreateBlock() {
    if (blockForm.professionalIds.length === 0 || !blockForm.startDate || !blockForm.endDate || !blockForm.startTime || !blockForm.endTime) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }
    const startDate = new Date(`${blockForm.startDate}T12:00:00`);
    const endDate = new Date(`${blockForm.endDate}T12:00:00`);
    if (endDate < startDate) { toast.error("A data de fim não pode ser anterior à data de início"); return; }
    const startTime = new Date(`${blockForm.startDate}T${blockForm.startTime}:00`);
    const endTime = new Date(`${blockForm.startDate}T${blockForm.endTime}:00`);
    if (endTime <= startTime) { toast.error("Horário de fim deve ser após o início"); return; }
    createBlock.mutate({
      professionalIds: blockForm.professionalIds.map(Number),
      startDate,
      endDate,
      startTime,
      endTime,
      reason: blockForm.reason || undefined,
    });
  }

  const filteredProfessionals = selectedProfId === "all" ? professionals : professionals.filter((p: any) => p.id === selectedProfId);

  // ─── Render helpers ──────────────────────────────────────────────────────────
  function renderHourLines() {
    return activeHours.flatMap((h: any) => [
      <div key={`${h}-hour`} className="absolute w-full border-t border-border/40" style={{ top: (h - startHour) * slotHeight }} />,
      <div key={`${h}-half`} className="absolute w-full border-t border-dashed border-border/25" style={{ top: (h - startHour) * slotHeight + slotHeight / 2 }} />,
    ]);
  }

  // Faixa cinza para horas fora do horário de funcionamento do salão
  function renderDayUnavailableOverlay(jsDay: number) {
    const range = getDayHourRange(jsDay);
    if (!range) return null;
    const totalH = activeHours.length;
    const beforeH = Math.max(0, range.open - startHour);
    const afterH = Math.max(0, startHour + totalH - range.close);
    return (
      <>
        {beforeH > 0 && (
          <div
            className="absolute left-0 right-0 bg-muted/50 border-b border-border/30 pointer-events-none"
            style={{ top: 0, height: beforeH * slotHeight, zIndex: 2 }}
            title="Fora do horário de funcionamento"
          />
        )}
        {afterH > 0 && (
          <div
            className="absolute left-0 right-0 bg-muted/50 border-t border-border/30 pointer-events-none"
            style={{ bottom: 0, height: afterH * slotHeight, zIndex: 2 }}
            title="Fora do horário de funcionamento"
          />
        )}
      </>
    );
  }

  // Faixa listrada para horas fora do horário do profissional
  function renderProfUnavailableOverlay(profId: number, jsDay: number) {
    const profHours = allProfWorkingHours.filter((h: any) => h.professionalId === profId);
    if (profHours.length === 0) return null; // sem configuração = não exibe overlay
    const dayEntry = profHours.find((h: any) => h.dayOfWeek === jsDay);
    if (!dayEntry) {
      // Profissional não atende neste dia — cobre tudo
      return (
        <div
          className="absolute left-0 right-0 bg-red-50/40 dark:bg-red-900/10 pointer-events-none"
          style={{ top: 0, bottom: 0, zIndex: 3 }}
          title="Profissional não atende neste dia"
        />
      );
    }
    const startH = parseInt(dayEntry.startTime.split(":")[0]);
    const endH = parseInt(dayEntry.endTime.split(":")[0]);
    const beforeH = Math.max(0, startH - startHour);
    const afterH = Math.max(0, startHour + activeHours.length - endH);
    return (
      <>
        {beforeH > 0 && (
          <div
            className="absolute left-0 right-0 bg-orange-50/50 dark:bg-orange-900/10 border-b border-orange-200/30 pointer-events-none"
            style={{ top: 0, height: beforeH * slotHeight, zIndex: 3 }}
            title="Fora do horário do profissional"
          />
        )}
        {afterH > 0 && (
          <div
            className="absolute left-0 right-0 bg-orange-50/50 dark:bg-orange-900/10 border-t border-orange-200/30 pointer-events-none"
            style={{ bottom: 0, height: afterH * slotHeight, zIndex: 3 }}
            title="Fora do horário do profissional"
          />
        )}
      </>
    );
  }

  function renderBlocks(dateStr: string, profId: number | "all") {
    return blocks
      .filter((b: any) => {
        const bDate = toLocalDateStr(new Date(b.startTime));
        return bDate === dateStr && (profId === "all" || b.professionalId === profId);
      })
      .map((block: any) => {
        const top = getTopPx(new Date(block.startTime), startHour, slotHeight);
        const height = getHeightPx(new Date(block.startTime), new Date(block.endTime), slotHeight);
        return (
          <div
            key={block.id}
            className="absolute left-0.5 right-0.5 rounded border border-gray-400 bg-gray-200/70 dark:bg-gray-700/70 px-1 py-0.5 overflow-hidden group"
            style={{ top, height, zIndex: 5 }}
          >
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-medium text-muted-foreground truncate flex items-center gap-0.5">
                <Lock className="w-2.5 h-2.5" />
                {block.reason ?? "Bloqueado"}
              </p>
              <button className="opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => deleteBlock.mutate({ id: block.id })}>
                <X className="w-3 h-3 text-destructive" />
              </button>
            </div>
          </div>
        );
      });
  }

  // ─── WEEK VIEW ───────────────────────────────────────────────────────────────
  function renderWeekView() {
    // No mobile: cada dia tem 80px de largura + 56px da coluna de hora = 7*80+56 = 616px min
    // No desktop: flex-1 distribui igualmente
    const timeColWidth = isMobile ? 44 : 56;
    const minTotalWidth = isMobile ? 7 * 80 + timeColWidth : 800;

    return (
      <>
        {/* ── Cabeçalho fixo dos dias da semana ── */}
        <div ref={weekHeaderScrollRef} className="flex flex-shrink-0 border-b border-border bg-background overflow-x-auto" style={{ scrollbarWidth: "none" }}>
            <div style={{ display: "flex", minWidth: minTotalWidth, width: "100%" }}>
              {/* Célula vazia alinhada com a coluna de horas */}
              <div className="flex-shrink-0 border-r border-border" style={{ width: timeColWidth }} />
              {weekDates.map((date, dayIdx) => {
                const isToday = date.toDateString() === new Date().toDateString();
                const jsDay = date.getDay();
                const dayOpen = isDayOpen(jsDay);
                const dateStr = toLocalDateStr(date);
                const occupancyAppts = appointments.filter((appt: any) => {
                  if (appt.status === "cancelled" || toLocalDateStr(new Date(appt.startTime)) !== dateStr) return false;
                  if (selectedProfId === "all") return true;
                  if (appt.professionalId === selectedProfId) return true;
                  return (appt.extraServices ?? []).some((service: any) => service.professionalId === selectedProfId);
                });
                const occupiedMinutes = occupancyAppts.reduce((total: number, appt: any) => (
                  total + Math.max(0, (new Date(appt.endTime).getTime() - new Date(appt.startTime).getTime()) / 60000)
                ), 0);
                const activeProfessionalCount = selectedProfId === "all" ? Math.max(1, filteredProfessionals.length) : 1;
                const capacityMinutes = Math.max(60, activeHours.length * 60 * activeProfessionalCount);
                const occupiedHours = Math.round((occupiedMinutes / 60) * 10) / 10;
                const capacityHours = Math.round((capacityMinutes / 60) * 10) / 10;
                return (
                  <div
                    key={dayIdx}
                    className={`h-16 border-r border-border last:border-r-0 flex flex-col items-center justify-center ${
                      !dayOpen ? "bg-muted/60" : isToday ? "bg-primary/10" : ""
                    }`}
                  style={isMobile ? { minWidth: 80, flex: "0 0 80px" } : { flex: 1 }}
                >
                  <span className="text-xs text-muted-foreground">{DAY_NAMES[dayIdx]}</span>
                  <span className={`text-sm font-semibold ${isToday ? "text-primary" : !dayOpen ? "text-muted-foreground/50" : "text-foreground"}`}>{date.getDate()}</span>
                  {isToday && <span className="text-[9px] font-bold text-primary bg-primary/20 px-1.5 rounded-full">Hoje</span>}
                  {!isToday && !dayOpen && <span className="text-[9px] text-muted-foreground/60">Fechado</span>}
                  {dayOpen && (
                    <span
                      className="text-[9px] leading-none text-muted-foreground mt-0.5"
                      title={`${occupancyAppts.length} agendamento(s), ${occupiedHours}h de ${capacityHours}h disponíveis`}
                    >
                      {occupiedHours}h/{capacityHours}h
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Grade de horários (rola verticalmente e horizontalmente no mobile) ── */}
        <div ref={weekGridScrollRef} className="flex-1 overflow-auto" style={{ WebkitOverflowScrolling: "touch" }}>
          <div className="flex" style={{ minWidth: minTotalWidth }}>
            {/* Time column */}
            <div className="flex-shrink-0 border-r border-border" style={{ width: timeColWidth }}>
              <div className="relative" style={{ height: slotHeight * activeHours.length }}>
                {activeHours.flatMap((h: any) => [
                  <div key={`${h}-hour`} className="absolute w-full flex items-start justify-end pr-2 text-xs text-muted-foreground" style={{ top: (h - startHour) * slotHeight - 8 }}>
                    {String(h).padStart(2, "0")}:00
                  </div>,
                  <div key={`${h}-half`} className="absolute w-full flex items-start justify-end pr-2 text-[10px] text-muted-foreground/70" style={{ top: (h - startHour) * slotHeight + slotHeight / 2 - 6 }}>
                    {String(h).padStart(2, "0")}:30
                  </div>,
                ])}
              </div>
            </div>

            {weekDates.map((date, dayIdx) => {
              const dateStr = toLocalDateStr(date);
              const jsDay = date.getDay();
              const dayOpen = isDayOpen(jsDay);
              const dayAppts = appointments.filter((a: any) => {
                const aDate = toLocalDateStr(new Date(a.startTime));
                if (aDate !== dateStr || a.status === "cancelled") return false;
                if (selectedProfId === "all") return true;
                if (a.professionalId === selectedProfId) return true;
                const extras2: any[] = a.extraServices ?? [];
                return extras2.some((es: any) => es.professionalId != null && es.professionalId === selectedProfId);
              });
              const colId = `col-${dateStr}-${selectedProfId === "all" ? (filteredProfessionals[0]?.id ?? 0) : selectedProfId}`;
              return (
                <div key={dayIdx} className="border-r border-border last:border-r-0" style={isMobile ? { minWidth: 80, flex: "0 0 80px" } : { flex: 1 }}>
                  <DroppableColumn
                    id={colId}
                    startHour={startHour}
                    slotHeight={slotHeight}
                    onSlotTap={isMobile ? (h, m) => openQuickAdd(dateStr, h, m, selectedProfId !== 'all' ? selectedProfId as number : undefined) : undefined}
                  >
                    {renderHourLines()}
                    {/* Overlay: dia fechado */}
                    {!dayOpen && (
                      <div className="absolute inset-0 bg-muted/40 pointer-events-none" style={{ zIndex: 2 }} title="Salão fechado" />
                    )}
                    {/* Overlay: fora do horário de funcionamento */}
                    {dayOpen && renderDayUnavailableOverlay(jsDay)}
                    {/* Overlay: fora do horário do profissional */}
                    {dayOpen && selectedProfId !== "all" && renderProfUnavailableOverlay(selectedProfId as number, jsDay)}
                    {renderBlocks(dateStr, selectedProfId)}
                    {(() => {
                      const colMap = computeColumns(dayAppts);
                      return dayAppts.map((appt: any) => {
                        const prof = professionals.find((p: any) => p.id === appt.professionalId);
                        const layout = colMap.get(appt.id) ?? { col: 0, totalCols: 1 };
                        return (
                          <DraggableApptCard
                            key={appt.id}
                            appt={appt}
                            profName={prof?.name ?? ""}
                            profAvatarUrl={prof?.avatarUrl}
                            profColor={prof?.color}
                            startHour={startHour}
                            slotHeight={slotHeight}
                            density={agendaDensity}
                            onEdit={openEditModal}
                            onOpenCheckout={(id) => navigate(`/checkout?appointmentId=${id}`)}
                            onRegisterArrival={handleAgendaArrival}
                            onUpdateStatus={handleAgendaStatusUpdate}
                            col={layout.col}
                            totalCols={layout.totalCols}
                            salonId={mySalon?.id}
                            currentTime={agendaClock}
                          />
                        );
                      });
                    })()}
                  </DroppableColumn>
                </div>
              );
            })}
          </div>
        </div>
      </>
    );
  }

  // ─── STAFF VIEW (colunas por profissional para o dia selecionado) ─────────────
  const [staffViewDate, setStaffViewDate] = useState(() => new Date());
  const dayAgendaScrollRef = useRef<HTMLDivElement>(null);
  // Mobile: profissional ativo nas abas (null = primeiro disponível)
  const [mobileProfId, setMobileProfId] = useState<number | null>(null);

  // Swipe para navegar entre dias no staff view (mobile)
  const goStaffPrev = useCallback(() => {
    setStaffViewDate(d => { const n = new Date(d); n.setDate(n.getDate() - 1); return n; });
  }, []);
  const goStaffNext = useCallback(() => {
    setStaffViewDate(d => { const n = new Date(d); n.setDate(n.getDate() + 1); return n; });
  }, []);
  const goToTodayAgenda = useCallback(() => {
    const today = new Date();
    setCurrentWeek(today);
    setStaffViewDate(today);
    if (linkedProfId) {
      setSelectedProfId(linkedProfId);
      setMobileProfId(linkedProfId);
      setViewMode("staff");
      return;
    }
    setViewMode("day");
  }, [linkedProfId]);
  const staffSwipeRef = useSwipeNavigation({
    enabled: isMobile && viewMode === "staff",
    threshold: 60,
    velocityThreshold: 0.3,
    onSwipeLeft: goStaffNext,
    onSwipeRight: goStaffPrev,
  });

  const scrollMobileAgendaToNow = useCallback(() => {
    const activeScrollArea = viewMode === "staff" ? staffSwipeRef.current : dayAgendaScrollRef.current;
    if (!activeScrollArea) return;
    activeScrollArea.scrollTo({
      top: getMobileAgendaNowScrollTop(new Date(), startHour, SLOT_HEIGHT),
      behavior: "smooth",
    });
  }, [viewMode, startHour, staffSwipeRef]);

  const goToNowInMobileAgenda = useCallback(() => {
    setStaffViewDate(new Date());
    window.setTimeout(scrollMobileAgendaToNow, 80);
  }, [scrollMobileAgendaToNow]);

  // Query separada para a staff view — carrega agendamentos do dia exato selecionado
  const staffQueryInput = useMemo(() => {
    const dayStart = new Date(staffViewDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(staffViewDate);
    dayEnd.setHours(23, 59, 59, 999);
    return { startDate: dayStart, endDate: dayEnd };
  }, [staffViewDate]);
  const { data: staffDayAppointments = [] } = trpc.appointments.getByDateRange.useQuery(
    staffQueryInput,
    { enabled: viewMode === "staff" || viewMode === "day" }
  );

  function renderDayView() {
    const dateStr = toLocalDateStr(staffViewDate);
    const isToday = staffViewDate.toDateString() === new Date().toDateString();
    const jsDay = staffViewDate.getDay();
    // Profissionais vinculados visualizam apenas a própria agenda, inclusive no atalho Hoje.
    const dayAppts = staffDayAppointments.filter((a: any) => {
      const aDate = toLocalDateStr(new Date(a.startTime));
      if (aDate !== dateStr || a.status === "cancelled") return false;
      if (!linkedProfId) return true;
      if (a.professionalId === linkedProfId) return true;
      return (a.extraServices ?? []).some((service: any) => service.professionalId === linkedProfId);
    });
    // Deduplicar por id (um agendamento pode aparecer em múltiplos profissionais)
    const uniqueAppts = Array.from(new Map(dayAppts.map((a: any) => [a.id, a])).values());
    const colMap = computeColumns(uniqueAppts);

    // Em telefone, uma grade de horários transforma nomes e serviços em faixas
    // horizontais muito pequenas. A lista cronológica mantém a mesma informação,
    // mas oferece texto grande, contraste e área de toque para pessoas com baixa visão.
    if (isMobile) {
      const sortedAppts = [...uniqueAppts].sort((a: any, b: any) =>
        new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
      );
      const mobileStatus: Record<string, { label: string; accent: string; pill: string }> = {
        pending: { label: "Pendente", accent: "border-l-amber-500", pill: "bg-amber-100 text-amber-900" },
        confirmed: { label: "Confirmado", accent: "border-l-emerald-600", pill: "bg-emerald-100 text-emerald-900" },
        in_service: { label: "Em atendimento", accent: "border-l-violet-600", pill: "bg-violet-100 text-violet-900" },
        completed: { label: "Concluído", accent: "border-l-sky-600", pill: "bg-sky-100 text-sky-900" },
        cancelled: { label: "Cancelado", accent: "border-l-rose-500", pill: "bg-rose-100 text-rose-900" },
        no_show: { label: "Não compareceu", accent: "border-l-slate-500", pill: "bg-slate-100 text-slate-800" },
      };

      return (
        <div className="flex h-full flex-col bg-background">
          <div className="flex items-center gap-2 border-b border-border bg-background px-3 py-2.5">
            <Button variant="outline" size="icon" className="h-11 w-11 shrink-0" onClick={goStaffPrev} aria-label="Ver dia anterior">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <button
              className={`min-h-11 flex-1 rounded-xl px-2 text-center transition-colors ${isToday ? "bg-primary/10 text-primary" : "bg-muted/45 text-foreground"}`}
              onClick={() => setStaffViewDate(new Date())}
              aria-label="Voltar para a agenda de hoje"
            >
              <span className="block text-base font-bold leading-5">{formatCompactAgendaDayHeader(staffViewDate)}</span>
              {isToday && <span className="mt-0.5 inline-block rounded-full bg-primary/15 px-2 py-0.5 text-xs font-semibold">Hoje</span>}
            </button>
            <Button variant="outline" size="icon" className="h-11 w-11 shrink-0" onClick={goStaffNext} aria-label="Ver próximo dia">
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>

          <div className="flex items-center justify-between gap-3 border-b border-border bg-muted/30 px-4 py-2.5">
            <div>
              <p className="text-base font-bold text-foreground">{sortedAppts.length} agendamento{sortedAppts.length !== 1 ? "s" : ""}</p>
              <p className="text-sm text-muted-foreground">Em ordem de horário</p>
            </div>
            {!isDayOpen(jsDay) && <span className="rounded-lg bg-rose-100 px-2.5 py-1.5 text-sm font-semibold text-rose-900">Salão fechado</span>}
          </div>

          <div className="flex-1 overflow-y-auto bg-muted/15 px-3 py-3 pb-28" style={{ WebkitOverflowScrolling: "touch" }}>
            <p className="mb-3 px-1 text-sm leading-5 text-muted-foreground">Toque em um atendimento para acessar ações rápidas ou ver os detalhes.</p>
            {sortedAppts.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border bg-background px-5 py-10 text-center">
                <CalendarDays className="mx-auto mb-3 h-9 w-9 text-muted-foreground/60" />
                <p className="text-base font-semibold text-foreground">Nenhum agendamento neste dia</p>
                <p className="mt-1 text-sm text-muted-foreground">Toque no botão + para criar um novo atendimento.</p>
              </div>
            ) : (
              <ol className="space-y-3" aria-label="Atendimentos do dia">
                {sortedAppts.map((appt: any) => {
                  const prof = filteredProfessionals.find((p: any) => p.id === appt.professionalId);
                  const state = mobileStatus[appt.status] ?? { label: appt.status ?? "Agendado", accent: "border-l-slate-400", pill: "bg-slate-100 text-slate-800" };
                  const start = formatTime(new Date(appt.startTime));
                  const end = formatTime(new Date(appt.endTime));
                  return (
                    <li key={appt.id}>
                      <button
                        type="button"
                        onClick={() => setMobileAgendaQuickActionAppt(appt)}
                        className={`w-full min-h-[86px] rounded-2xl border border-border border-l-[6px] ${state.accent} bg-card p-3.5 text-left shadow-sm transition-colors active:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2`}
                        aria-label={`${appt.clientName ?? "Cliente"}, ${appt.serviceName ?? "serviço"}, de ${start} até ${end}. ${state.label}. Abrir ações rápidas e detalhes.`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-[52px] shrink-0 rounded-xl bg-muted px-1 py-2 text-center">
                            <p className="text-lg font-bold leading-5 text-foreground">{start}</p>
                            <p className="mt-0.5 text-xs font-medium text-muted-foreground">até {end}</p>
                          </div>
                          {appt.clientPhotoUrl ? (
                            <img src={appt.clientPhotoUrl} alt="" className="h-11 w-11 shrink-0 rounded-full border border-border object-cover" />
                          ) : (
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-base font-bold text-white" style={{ backgroundColor: prof?.color ?? "#C9A96E" }} aria-hidden="true">
                              {(appt.clientName ?? "C").charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <p className="truncate text-base font-bold leading-5 text-foreground">{appt.clientName ?? "Cliente"}</p>
                              <span className={`shrink-0 rounded-full px-2 py-1 text-xs font-bold ${state.pill}`}>{state.label}</span>
                            </div>
                            <p className="mt-1 truncate text-sm font-semibold text-foreground/85">{appt.serviceName ?? "Serviço"}</p>
                            {prof?.name && <p className="mt-0.5 truncate text-sm text-muted-foreground">{prof.name}</p>}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full">
        {/* Date nav */}
        <div className="flex items-center gap-1 px-2 py-1 border-b border-border bg-background">
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={goStaffPrev}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <button
            className={`flex-1 flex flex-col items-center justify-center py-0.5 rounded-lg transition-colors ${isToday ? "bg-primary/10" : "hover:bg-muted/50"}`}
            onClick={() => setStaffViewDate(new Date())}
          >
            <span className={`text-sm font-semibold leading-4 ${isToday ? "text-primary" : "text-foreground"}`}>
              {formatCompactAgendaDayHeader(staffViewDate)}
            </span>
            {isToday && <span className="mt-0.5 rounded-full bg-primary/10 px-1.5 py-0 text-[9px] font-semibold leading-3 text-primary">Hoje</span>}
          </button>
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={goStaffNext}>
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
        {/* Summary bar */}
        <div className="flex items-center gap-3 px-4 py-1.5 bg-muted/30 border-b border-border text-xs text-muted-foreground">
          <span className="font-medium text-foreground">{uniqueAppts.length} agendamento{uniqueAppts.length !== 1 ? "s" : ""}</span>
          {!isDayOpen(jsDay) && <span className="text-red-500 font-medium">Salão fechado</span>}
        </div>
        {/* Grade única */}
        <div ref={dayAgendaScrollRef} className="flex-1 overflow-auto" style={{ WebkitOverflowScrolling: "touch" }}>
          <div className="flex" style={{ minWidth: 320 }}>
            {/* Time column */}
            <div className="w-14 flex-shrink-0 border-r border-border">
              <div className="relative" style={{ height: SLOT_HEIGHT * activeHours.length }}>
                {activeHours.map((h: any) => (
                  <div key={h} className="absolute w-full flex items-start justify-end pr-2 text-xs text-muted-foreground" style={{ top: (h - startHour) * SLOT_HEIGHT - 8 }}>
                    {String(h).padStart(2, "0")}:00
                  </div>
                ))}
              </div>
            </div>
            {/* Single day column */}
            <div className="flex-1">
              <DroppableColumn
                id={`col-${dateStr}-day-all`}
                startHour={startHour}
                onSlotTap={isMobile ? (h, m) => openQuickAdd(dateStr, h, m, linkedProfId ?? undefined) : undefined}
              >
                {renderHourLines()}
                {isDayOpen(jsDay) && renderDayUnavailableOverlay(jsDay)}
                {!isDayOpen(jsDay) && <div className="absolute inset-0 bg-muted/40 pointer-events-none" style={{ zIndex: 2 }} title="Salão fechado" />}
                {uniqueAppts.map((appt: any) => {
                  const layout = colMap.get(appt.id) ?? { col: 0, totalCols: 1 };
                  const prof = filteredProfessionals.find((p: any) => p.id === appt.professionalId);
                  return (
                    <DraggableApptCard
                      key={appt.id}
                      appt={appt}
                      profName={prof?.name ?? ""}
                      profAvatarUrl={prof?.avatarUrl}
                      profColor={prof?.color}
                      startHour={startHour}
                      onEdit={openEditModal}
                      onOpenCheckout={(id) => navigate(`/checkout?appointmentId=${id}`)}
                      onRegisterArrival={handleAgendaArrival}
                      onUpdateStatus={handleAgendaStatusUpdate}
                      col={layout.col}
                      totalCols={layout.totalCols}
                      salonId={mySalon?.id}
                      currentTime={agendaClock}
                    />
                  );
                })}
              </DroppableColumn>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderStaffView() {
    const dateStr = toLocalDateStr(staffViewDate);
    const isToday = staffViewDate.toDateString() === new Date().toDateString();
    const activeMobileProfId = mobileProfId ?? (filteredProfessionals[0]?.id ?? null);

    // Renderiza a coluna de um profissional específico
    function renderProfColumn(prof: any, fullWidth = false) {
      // Usa staffDayAppointments (query do dia exato) para evitar problema de datas fora da semana carregada
      // Inclui agendamentos onde o profissional é o principal OU executor de algum serviço extra
      const profAppts = staffDayAppointments.filter((a: any) => {
        const aDate = toLocalDateStr(new Date(a.startTime));
        if (aDate !== dateStr || a.status === "cancelled") return false;
        // Profissional principal do agendamento
        if (a.professionalId === prof.id) return true;
        // Profissional executor de algum serviço extra
        const extras: any[] = a.extraServices ?? [];
        return extras.some((es: any) => es.professionalId != null && es.professionalId === prof.id);
      });
      const colId = `col-${dateStr}-${prof.id}-staff`;
      const jsDay = staffViewDate.getDay();
      const profHoursForDay = allProfWorkingHours.filter((h: any) => h.professionalId === prof.id && h.dayOfWeek === jsDay);
      const profAttendsToday = allProfWorkingHours.filter((h: any) => h.professionalId === prof.id).length === 0 || profHoursForDay.length > 0;
      return (
        <div key={prof.id} className={`${fullWidth ? "flex-1" : "flex-1 border-r border-border last:border-r-0 min-w-[120px]"}`}>
          {/* Prof header - apenas no desktop (mobile usa as abas) */}
          {!fullWidth && (
            <div className={`h-16 border-b border-border flex flex-col items-center justify-center gap-0.5 ${
              !profAttendsToday ? "bg-red-50/30 dark:bg-red-900/10" : "bg-muted/30"
            }`}>
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0 overflow-hidden" style={{ backgroundColor: prof.color ?? "#C9A96E" }}>
                {prof.avatarUrl ? <img src={prof.avatarUrl} alt={prof.name} className="w-full h-full object-cover" /> : prof.name.charAt(0).toUpperCase()}
              </div>
              <span className="text-[10px] font-semibold text-foreground truncate px-1 max-w-full">{prof.name}</span>
              {profAttendsToday ? (
                <Badge variant="outline" className="text-[9px] h-4 px-1">{profAppts.length} agend.</Badge>
              ) : (
                <Badge variant="outline" className="text-[9px] h-4 px-1 border-red-300 text-red-500">Folga</Badge>
              )}
            </div>
          )}
          <DroppableColumn
            id={colId}
            startHour={startHour}
            onSlotTap={isMobile ? (h, m) => openQuickAdd(dateStr, h, m, prof.id) : undefined}
          >
            {renderHourLines()}
            {isDayOpen(jsDay) && renderDayUnavailableOverlay(jsDay)}
            {!isDayOpen(jsDay) && <div className="absolute inset-0 bg-muted/40 pointer-events-none" style={{ zIndex: 2 }} title="Salão fechado" />}
            {renderProfUnavailableOverlay(prof.id, jsDay)}
            {renderBlocks(dateStr, prof.id)}
            {(() => {
              const colMap = computeColumns(profAppts);
              return profAppts.map((appt: any) => {
                const layout = colMap.get(appt.id) ?? { col: 0, totalCols: 1 };
                // Verificar se este profissional é executor de serviço extra (não o principal)
                const isExtraExecutor = appt.professionalId !== prof.id;
                const executorService = isExtraExecutor
                  ? (appt.extraServices ?? []).find((es: any) => es.professionalId === prof.id)
                  : null;
                return (
                  <DraggableApptCard
                    key={`${appt.id}-${prof.id}`}
                    appt={appt}
                    profName={prof.name}
                    profAvatarUrl={prof?.avatarUrl}
                    profColor={prof?.color}
                    startHour={startHour}
                    onEdit={openEditModal}
                    onOpenCheckout={(id) => navigate(`/checkout?appointmentId=${id}`)}
                    onRegisterArrival={handleAgendaArrival}
                    onUpdateStatus={handleAgendaStatusUpdate}
                    col={layout.col}
                    totalCols={layout.totalCols}
                    isExtraExecutor={isExtraExecutor}
                    executorServiceName={executorService?.name}
                    salonId={mySalon?.id}
                    currentTime={agendaClock}
                  />
                );
              });
            })()}
          </DroppableColumn>
        </div>
      );
    }

    return (
      <div className="flex flex-col h-full">
        {/* Date nav — mobile: destaque maior para a data */}
        <div className="flex items-center gap-1 px-2 py-1 border-b border-border bg-background">
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={goStaffPrev}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <button
            className={`flex-1 flex flex-col items-center justify-center py-0.5 rounded-lg transition-colors ${isToday ? "bg-primary/10" : "hover:bg-muted/50"}`}
            onClick={() => setStaffViewDate(new Date())}
          >
            <span className={`text-sm font-semibold leading-4 ${isToday ? "text-primary" : "text-foreground"}`}>
              {formatCompactAgendaDayHeader(staffViewDate)}
            </span>
            {isToday && <span className="mt-0.5 rounded-full bg-primary/10 px-1.5 py-0 text-[9px] font-semibold leading-3 text-primary">Hoje</span>}
          </button>
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={goStaffNext}>
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>
        {/* Dica de swipe — apenas mobile, apenas uma vez */}
        {isMobile && (
          <div className="flex md:hidden items-center justify-center gap-1.5 py-1 text-[10px] text-muted-foreground/60">
            <ChevronLeft className="w-2.5 h-2.5" />
            <span>Deslize para mudar de dia</span>
            <ChevronRight className="w-2.5 h-2.5" />
          </div>
        )}
        {isMobile && <MobileAgendaActions onToday={() => setStaffViewDate(new Date())} onNow={goToNowInMobileAgenda} onSchedule={() => {
          const slot = getMobileAgendaQuickStartSlot(new Date());
          openQuickAdd(toLocalDateStr(staffViewDate), slot.hour, slot.minute, activeMobileProfId ?? undefined);
        }} />}

        {/* ─ MOBILE: abas deslizáveis por profissional ─ */}
        {filteredProfessionals.length > 1 && (
          <div className="flex md:hidden overflow-x-auto border-b border-border bg-muted/20 scrollbar-hide">
            {filteredProfessionals.map((prof: any) => {
              const isActive = (activeMobileProfId === prof.id);
              const profAppts = staffDayAppointments.filter((a: any) => {
                const aDate = toLocalDateStr(new Date(a.startTime));
                if (aDate !== dateStr || a.status === "cancelled") return false;
                // Profissional principal OU executor de serviço extra
                if (a.professionalId === prof.id) return true;
                const extras: any[] = a.extraServices ?? [];
                return extras.some((es: any) => es.professionalId != null && es.professionalId === prof.id);
              });
              const jsDay = staffViewDate.getDay();
              const profHoursForDay = allProfWorkingHours.filter((h: any) => h.professionalId === prof.id && h.dayOfWeek === jsDay);
              const profAttendsToday = allProfWorkingHours.filter((h: any) => h.professionalId === prof.id).length === 0 || profHoursForDay.length > 0;
              return (
                <button
                  key={prof.id}
                  onClick={() => setMobileProfId(prof.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 whitespace-nowrap text-sm font-medium border-b-2 transition-colors shrink-0 ${
                    isActive
                      ? "border-primary text-primary bg-background"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[9px] font-bold shrink-0 overflow-hidden"
                    style={{ backgroundColor: prof.color ?? "#C9A96E" }}
                  >
                    {prof.avatarUrl ? <img src={prof.avatarUrl} alt={prof.name} className="w-full h-full object-cover" /> : prof.name.charAt(0).toUpperCase()}
                  </div>
                  <span>{prof.name}</span>
                  <span className={`text-xs rounded-full px-1.5 py-0.5 ${
                    isActive ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
                  }`}>{profAppts.length}</span>
                  {!profAttendsToday && <span className="text-[10px] text-red-400">(folga)</span>}
                </button>
              );
            })}
          </div>
        )}

        {/* ─ MOBILE: coluna única do profissional ativo (com swipe) ─ */}
        <div className="flex md:hidden flex-1 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }} ref={staffSwipeRef as React.RefObject<HTMLDivElement>}>
          <div className="w-12 flex-shrink-0 border-r border-border">
            <div className="relative" style={{ height: SLOT_HEIGHT * activeHours.length }}>
              {activeHours.map((h: any) => (
                <div key={h} className="absolute w-full flex items-start justify-end pr-1.5 text-[10px] text-muted-foreground" style={{ top: (h - startHour) * SLOT_HEIGHT - 8 }}>
                  {String(h).padStart(2, "0")}:00
                </div>
              ))}
            </div>
          </div>
          {activeMobileProfId !== null && (() => {
            const prof = filteredProfessionals.find((p: any) => p.id === activeMobileProfId) ?? filteredProfessionals[0];
            if (!prof) return null;
            return renderProfColumn(prof, true);
          })()}
        </div>

        {/* ─ DESKTOP: todas as colunas lado a lado ─ */}
        <div className="hidden md:flex flex-1 min-h-0 overflow-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
        <div className="flex min-w-[600px]">
          {/* Time column */}
          <div className="w-14 flex-shrink-0 border-r border-border">
            <div className="h-12 border-b border-border" />
            <div className="relative" style={{ height: SLOT_HEIGHT * activeHours.length }}>
              {activeHours.map((h: any) => (
                <div key={h} className="absolute w-full flex items-start justify-end pr-2 text-xs text-muted-foreground" style={{ top: (h - startHour) * SLOT_HEIGHT - 8 }}>
                  {String(h).padStart(2, "0")}:00
                </div>
              ))}
            </div>
          </div>
          {filteredProfessionals.map((prof: any) => renderProfColumn(prof, false))}
        </div>
        </div>
      </div>
    );
  }

  // ─── Overlay card (shown while dragging) ─────────────────────────────────────
  function renderDragOverlay() {
    if (!draggingAppt) return null;
    const colorClass = STATUS_COLORS[draggingAppt.status] ?? "bg-slate-400/80 border-slate-500";
    return (
      <div className={`rounded border ${colorClass} px-2 py-1 shadow-lg opacity-90 pointer-events-none`} style={{ width: 120 }}>
        <p className="text-[11px] font-semibold truncate">{draggingAppt.clientName ?? "Cliente"}</p>
        <p className="text-[10px] opacity-70">{formatTime(new Date(draggingAppt.startTime))}</p>
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="flex flex-col h-full bg-background">
        {/* Header */}
        <div className="flex flex-col border-b border-border">
          {/* Linha principal: identificação, modo, ações e filtros */}
          <div className="flex flex-wrap lg:flex-nowrap items-center gap-2 px-3 py-2 md:px-4">
            <div className="flex shrink-0 items-center gap-1.5">
              <Calendar className="h-4 w-4 shrink-0 text-primary" />
              <h1 className="text-sm font-semibold text-foreground">{isMobile ? "Agenda do dia" : "Agenda semanal"}</h1>
            </div>

            {/* View mode toggle */}
            <div className="flex shrink-0 items-center gap-1 rounded-lg bg-muted p-0.5">
              {!isMobile && (
                <Button
                  variant={viewMode === "week" ? "default" : "ghost"}
                  size="sm"
                  className="h-7 px-2 text-xs md:px-3"
                  onClick={() => setViewMode("week")}
                >
                  <LayoutGrid className="w-3.5 h-3.5 md:mr-1" />
                  <span>Semana</span>
                </Button>
              )}
              <Button
                variant={viewMode === "staff" ? "default" : "ghost"}
                size="sm"
                className={isMobile ? "h-11 px-3 text-sm" : "h-7 px-2 text-xs md:px-3"}
                onClick={() => setViewMode("staff")}
              >
                <Users className={isMobile ? "mr-1 h-4 w-4" : "w-3.5 h-3.5 md:mr-1"} />
                <span>{isMobile ? "Profissional" : "Por Profissional"}</span>
              </Button>
              <Button
                variant={viewMode === "day" ? "default" : "ghost"}
                size="sm"
                className={isMobile ? "h-11 px-3 text-sm" : "h-7 px-2 text-xs md:px-3"}
                onClick={goToTodayAgenda}
              >
                <CalendarDays className={isMobile ? "mr-1 h-4 w-4" : "w-3.5 h-3.5 md:mr-1"} />
                <span>Dia</span>
              </Button>
            </div>

            {/* Ações e filtros — alinhados na mesma linha em desktop */}
            {!isMobile && <div className="flex min-w-0 items-center gap-1.5 lg:ml-auto">
              <Button size="sm" variant="default" className="h-8 px-2.5 text-xs" onClick={openNewApptModal}>
                <Plus className="w-4 h-4 mr-1" />
                Novo Agendamento
              </Button>
              {!linkedProfId && (branches as any[]).length > 0 && (
                <Select value={String(selectedBranchId)} onValueChange={(v) => {
                  setSelectedBranchId(v === "all" ? "all" : Number(v));
                  setSelectedProfId("all");
                }}>
                  <SelectTrigger className="h-8 w-36 text-xs">
                    <GitBranch className="mr-1.5 h-3.5 w-3.5 shrink-0" />
                    <SelectValue placeholder="Todas as filiais" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as filiais</SelectItem>
                    {(branches as any[]).map((b: any) => (
                      <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {!linkedProfId && (
                <Select value={String(selectedProfId)} onValueChange={(v) => setSelectedProfId(v === "all" ? "all" : Number(v))}>
                  <SelectTrigger className="h-8 w-40 text-xs">
                    <SelectValue placeholder="Todos os profissionais" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os profissionais</SelectItem>
                    {professionals.map((p: any) => (
                      <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {linkedProfId && (
                <div className="flex items-center gap-1.5 rounded-md bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                  <Users className="h-3.5 w-3.5" />
                  {(professionals as any[]).find((p: any) => p.id === linkedProfId)?.name ?? "Minha Agenda"}
                </div>
              )}
              <Button size="sm" variant="outline" className="h-8 px-2.5 text-xs" onClick={() => setBlockModalOpen(true)}>
                <Lock className="w-4 h-4 mr-1" />
                Bloquear
              </Button>
            </div>}
            {/* Botão Bloquear no mobile — ícone apenas */}
            {isMobile && <div className="flex">
              <Button size="sm" variant="outline" className="h-11 w-11 p-0" onClick={() => setBlockModalOpen(true)} title="Bloquear horário" aria-label="Bloquear horário">
                <Lock className="h-5 w-5" />
              </Button>
            </div>}
          </div>

          {/* Linha 2: navegação de semana e densidade */}
          <div className="flex flex-wrap md:flex-nowrap items-center gap-1.5 px-3 md:px-6 pb-2 min-w-0">
            {viewMode === "week" && (
              <div className="flex items-center gap-1 w-full md:flex-1 min-w-0">
                <Button variant="outline" size="sm" className="h-7 text-xs shrink-0" onClick={goToTodayAgenda}>Hoje</Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => { const d = new Date(currentWeek); d.setDate(d.getDate() - 7); setCurrentWeek(d); }}>
                  <ChevronLeft className="w-3.5 h-3.5" />
                </Button>
                <span className="text-xs font-medium text-foreground text-center truncate flex-1">
                  {weekDates[0]!.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })} – {weekDates[6]!.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })}
                </span>
                <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => { const d = new Date(currentWeek); d.setDate(d.getDate() + 7); setCurrentWeek(d); }}>
                  <ChevronRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}

            {/* Densidade da agenda no desktop */}
            {!isMobile && <div className="flex items-center gap-2 ml-auto">
              {viewMode === "week" && (
                <Select value={agendaDensity} onValueChange={(value) => {
                  const density = value as AgendaDensity;
                  setAgendaDensity(density);
                  localStorage.setItem(AGENDA_DENSITY_KEY, density);
                }}>
                  <SelectTrigger className="w-32 h-8 text-sm" aria-label="Densidade da agenda">
                    <SelectValue placeholder="Densidade" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(AGENDA_DENSITIES).map(([value, config]) => <SelectItem key={value} value={value}>{config.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>}

            {/* Seletor de profissional no mobile (week view) */}
            {isMobile && viewMode === "week" && !linkedProfId && (
              <div className="flex w-full">
                <Select value={String(selectedProfId)} onValueChange={(v) => setSelectedProfId(v === "all" ? "all" : Number(v))}>
                  <SelectTrigger className="w-full h-8 text-xs">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {professionals.map((p: any) => (
                      <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        {canViewTanningArrivalIntents && (
          <section className="border-b border-amber-200 bg-amber-50/55 px-3 py-1.5 md:px-4" data-testid="agenda-tanning-arrival-intents">
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5 shrink-0 text-amber-700" />
                <h2 className="shrink-0 text-xs font-semibold text-amber-950">Intenções de chegada — Bronze</h2>
                <Badge variant="outline" className="h-5 shrink-0 border-amber-300 bg-background/70 px-1.5 text-[10px] text-amber-800">Sem reserva</Badge>
                <p className="hidden truncate text-[11px] text-amber-900/75 lg:block">Duplo clique abre a Agenda para confirmação; a cliente entra na fila somente quando chegar.</p>
              </div>
              <span className="shrink-0 text-[11px] font-medium text-amber-800">{loadingTanningArrivalIntents ? "Atualizando..." : `${tanningArrivalIntents.length} registro${tanningArrivalIntents.length === 1 ? "" : "s"}`}</span>
            </div>
            {!loadingTanningArrivalIntents && tanningArrivalIntents.length > 0 && (
              <div className="mt-1.5 flex gap-1.5 overflow-x-auto pb-0.5" aria-live="polite">
                {tanningArrivalIntents.map((intent: any) => (
                  <button
                    key={intent.id}
                    type="button"
                    onDoubleClick={() => openArrivalIntentAsNewAppointment(intent)}
                    className="min-w-[184px] rounded-md border border-amber-200 bg-background/85 px-2.5 py-1.5 text-left transition-colors hover:border-amber-400 hover:bg-amber-100/55 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                    title="Dê duplo clique para abrir o novo agendamento com estes dados preenchidos. Nada é salvo automaticamente."
                    aria-label={`Abrir intenção de ${intent.clientName} no formulário de novo agendamento`}
                    data-testid={`arrival-intent-open-${intent.id}`}
                  >
                    <div className="flex min-w-0 items-center gap-1">
                      <p className="min-w-0 truncate text-xs font-semibold text-foreground">{intent.clientName}</p>
                      <span className="flex min-w-0 shrink items-center gap-0.5 truncate text-[10px] text-muted-foreground"><Phone className="h-2.5 w-2.5 shrink-0" />{intent.clientPhone || "Telefone não informado"}</span>
                    </div>
                    <div className="mt-0.5 flex items-center justify-between gap-2 text-[11px]">
                      <p className="min-w-0 truncate font-medium text-amber-900">{intent.serviceNames?.join(" · ") || "Tipo de bronze não informado"}</p>
                      <p className="shrink-0 text-amber-800">{formatTanningArrivalIntentForAgenda(intent)}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {!loadingTanningArrivalIntents && tanningArrivalIntents.length === 0 && <p className="mt-1.5 text-[11px] text-amber-900/70">Nenhuma intenção de chegada de bronze nesta semana.</p>}
          </section>
        )}

        {/* Calendar Grid */}
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          {viewMode === "week" ? renderWeekView() : viewMode === "day" ? renderDayView() : renderStaffView()}
        </div>

        {/* Legend — oculta no mobile para economizar espaço */}
        <div className="hidden md:flex items-center gap-4 px-6 py-2 border-t border-border text-xs text-muted-foreground flex-wrap">
          <span className="font-medium">Legenda:</span>
          {Object.entries({ pending: "Pendente", confirmed: "Confirmado", in_service: "Em atendimento", completed: "Concluído", cancelled: "Cancelado" }).map(([k, v]) => (
            <span key={k} className="inline-flex items-center gap-1">
              <span className={`w-3 h-3 rounded border ${STATUS_COLORS[k]}`} />
              {v}
            </span>
          ))}
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded border border-gray-400 bg-gray-200/70" />
            Bloqueado
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded border border-border bg-muted/50" />
            Fora do expediente
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded border border-orange-200 bg-orange-50/50" />
            Fora do horário do profissional
          </span>
          <span className="h-4 w-px bg-border mx-1" aria-hidden="true" />
          <span className="font-medium">Origem:</span>
          {(["professional", "ai_native", "reception", "admin"] as const).map((origin) => {
            const originInfo = bookingOriginPresentation[origin];
            return (
              <span key={origin} className="flex items-center gap-1" title={originInfo.label}>
                <span className={`min-w-6 rounded border px-1 py-px text-center text-[9px] font-bold ${originInfo.tone}`}>
                  {originInfo.compact}
                </span>
                {originInfo.label}
              </span>
            );
          })}
          <span className="ml-auto text-muted-foreground/60">Arraste para mover · Duplo clique para editar</span>
        </div>
      </div>

      {/* Drag Overlay */}
      <DragOverlay>{renderDragOverlay()}</DragOverlay>

      {/* ─── Ações rápidas da agenda (mobile) ───────────────────────────────────────────── */}
      <Sheet open={!!mobileAgendaQuickActionAppt} onOpenChange={(open) => { if (!open) setMobileAgendaQuickActionAppt(null); }}>
        <SheetContent side="bottom" className="max-h-[88dvh] rounded-t-2xl px-0 pb-[calc(1rem+env(safe-area-inset-bottom))]">
          {mobileAgendaQuickActionAppt && (() => {
            const appt = mobileAgendaQuickActionAppt;
            const prof = (professionals as any[]).find((item: any) => item.id === appt.professionalId);
            const statusInfo: Record<string, { label: string; pill: string }> = {
              pending: { label: "Pendente", pill: "bg-amber-100 text-amber-900" },
              confirmed: { label: "Confirmado", pill: "bg-emerald-100 text-emerald-900" },
              in_service: { label: "Em atendimento", pill: "bg-violet-100 text-violet-900" },
              completed: { label: "Concluído", pill: "bg-sky-100 text-sky-900" },
              cancelled: { label: "Cancelado", pill: "bg-rose-100 text-rose-900" },
              no_show: { label: "Não compareceu", pill: "bg-slate-100 text-slate-800" },
            };
            const status = statusInfo[appt.status] ?? { label: appt.status ?? "Agendado", pill: "bg-slate-100 text-slate-800" };
            const canRegisterArrival = brasiliaDateString(new Date(appt.startTime)) === brasiliaDateString()
              && !["cancelled", "completed", "no_show"].includes(appt.status);
            const hasQuickAction = canRegisterArrival || appt.status === "confirmed" || appt.status === "in_service";
            const actionIsPending = registerAgendaArrival.isPending || updateAgendaStatus.isPending;

            return (
              <>
                <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-muted-foreground/20" aria-hidden="true" />
                <SheetHeader className="border-b border-border px-5 pb-4 pt-3 text-left">
                  <div className="flex min-w-0 items-start gap-3 pr-7">
                    {appt.clientPhotoUrl ? (
                      <img src={appt.clientPhotoUrl} alt="" className="h-12 w-12 shrink-0 rounded-full border border-border object-cover" />
                    ) : (
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-lg font-bold text-white" style={{ backgroundColor: prof?.color ?? "#C9A96E" }} aria-hidden="true">
                        {(appt.clientName ?? "C").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <SheetTitle className="truncate text-left text-lg">{appt.clientName ?? "Cliente"}</SheetTitle>
                      <SheetDescription className="mt-0.5 truncate text-left">Ações rápidas do atendimento</SheetDescription>
                    </div>
                    <span className={`shrink-0 rounded-full px-2 py-1 text-xs font-bold ${status.pill}`}>{status.label}</span>
                  </div>
                </SheetHeader>

                <div className="space-y-3 overflow-y-auto px-5 py-4">
                  <div className="rounded-xl border border-border bg-muted/35 p-3.5 text-sm">
                    <div className="flex items-start gap-2.5">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-muted-foreground">Serviço</p>
                        <p className="font-semibold text-foreground">{appt.serviceName ?? "Serviço"}</p>
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-3 border-t border-border pt-3">
                      <div className="flex min-w-0 items-start gap-2">
                        <Clock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        <div>
                          <p className="text-xs font-medium text-muted-foreground">Horário</p>
                          <p className="font-semibold text-foreground">{formatTime(new Date(appt.startTime))} – {formatTime(new Date(appt.endTime))}</p>
                        </div>
                      </div>
                      <div className="flex min-w-0 items-start gap-2">
                        <Users className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-muted-foreground">Profissional</p>
                          <p className="truncate font-semibold text-foreground">{prof?.name ?? appt.professionalName ?? "Não informado"}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2" aria-live="polite">
                    <p className="px-1 text-sm font-semibold text-foreground">Ações do atendimento</p>
                    {canRegisterArrival && (
                      <Button
                        className="min-h-12 w-full justify-start bg-amber-500 text-base text-white hover:bg-amber-600"
                        disabled={actionIsPending}
                        onClick={() => {
                          handleAgendaArrival(appt.id);
                          setMobileAgendaQuickActionAppt(null);
                        }}
                      >
                        <Users className="mr-2 h-5 w-5" />
                        Chegou
                      </Button>
                    )}
                    {appt.status === "confirmed" && (
                      <Button
                        className="min-h-12 w-full justify-start bg-violet-600 text-base text-white hover:bg-violet-700"
                        disabled={actionIsPending}
                        onClick={() => {
                          handleAgendaStatusUpdate(appt.id, "in_service");
                          setMobileAgendaQuickActionAppt(null);
                        }}
                      >
                        <CheckCircle2 className="mr-2 h-5 w-5" />
                        Iniciar atendimento
                      </Button>
                    )}
                    {appt.status === "in_service" && (
                      <Button
                        className="min-h-12 w-full justify-start bg-sky-600 text-base text-white hover:bg-sky-700"
                        disabled={actionIsPending}
                        onClick={() => {
                          handleAgendaStatusUpdate(appt.id, "completed");
                          setMobileAgendaQuickActionAppt(null);
                        }}
                      >
                        <CheckCircle2 className="mr-2 h-5 w-5" />
                        Concluir
                      </Button>
                    )}
                    {!hasQuickAction && <p className="rounded-xl bg-muted px-3 py-3 text-sm text-muted-foreground">Este atendimento não possui ações operacionais rápidas disponíveis.</p>}
                  </div>

                  <Button
                    variant="outline"
                    className="min-h-12 w-full text-base"
                    onClick={() => {
                      setMobileAgendaQuickActionAppt(null);
                      openEditModal(appt);
                    }}
                  >
                    <Edit2 className="mr-2 h-5 w-5" />
                    Ver/editar detalhes
                  </Button>
                </div>
              </>
            );
          })()}
        </SheetContent>
      </Sheet>

      {/* Confirm Move Dialog */}
      <Dialog open={!!confirmMove} onOpenChange={(o) => { if (!o) setConfirmMove(null); }}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar Movimentação</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">Deseja mover este agendamento?</p>
            <div className="bg-muted rounded-lg p-3 space-y-1">
              <p><span className="font-medium">De:</span> {confirmMove?.fromLabel}</p>
              <p><span className="font-medium">Para:</span> {confirmMove?.toLabel}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmMove(null)}>Cancelar</Button>
            <Button
              onClick={() => {
                if (!confirmMove) return;
                moveAppt.mutate({
                  id: confirmMove.apptId,
                  newStartTime: confirmMove.newStartTime,
                  newEndTime: confirmMove.newEndTime,
                  newProfessionalId: confirmMove.newProfessionalId,
                });
              }}
              disabled={moveAppt.isPending}
            >
              {moveAppt.isPending ? "Movendo..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Block Modal */}
      <Dialog open={blockModalOpen} onOpenChange={setBlockModalOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-4 h-4" /> Bloquear Horário
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Label>Profissionais *</Label>
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                  <Checkbox
                    checked={professionals.length > 0 && blockForm.professionalIds.length === professionals.length}
                    onCheckedChange={(checked) => setBlockForm((f) => ({ ...f, professionalIds: checked ? professionals.map((p: any) => String(p.id)) : [] }))}
                  />
                  Selecionar todas
                </label>
              </div>
              <div className="max-h-44 overflow-y-auto rounded-md border border-input bg-background p-2 space-y-1.5">
                {professionals.map((p: any) => {
                  const id = String(p.id);
                  const selected = blockForm.professionalIds.includes(id);
                  return (
                    <label key={id} className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted cursor-pointer">
                      <Checkbox
                        checked={selected}
                        onCheckedChange={(checked) => setBlockForm((f) => ({
                          ...f,
                          professionalIds: checked ? [...f.professionalIds, id] : f.professionalIds.filter((professionalId) => professionalId !== id),
                        }))}
                      />
                      <span>{p.name}</span>
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">{blockForm.professionalIds.length === 0 ? "Selecione uma ou mais profissionais." : `${blockForm.professionalIds.length} ${blockForm.professionalIds.length === 1 ? "profissional selecionada" : "profissionais selecionadas"}.`}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data de início *</Label>
                <Input
                  type="date"
                  value={blockForm.startDate}
                  onChange={(e) => setBlockForm((f) => ({
                    ...f,
                    startDate: e.target.value,
                    endDate: !f.endDate || f.endDate < e.target.value ? e.target.value : f.endDate,
                  }))}
                />
              </div>
              <div>
                <Label>Data de fim *</Label>
                <Input
                  type="date"
                  min={blockForm.startDate || undefined}
                  value={blockForm.endDate}
                  onChange={(e) => setBlockForm((f) => ({ ...f, endDate: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Início *</Label>
                <Input type="time" value={blockForm.startTime} onChange={(e) => setBlockForm((f) => ({ ...f, startTime: e.target.value }))} />
              </div>
              <div>
                <Label>Fim *</Label>
                <Input type="time" value={blockForm.endTime} onChange={(e) => setBlockForm((f) => ({ ...f, endTime: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Motivo</Label>
              <Input placeholder="Ex: Almoço, Reunião, Folga..." value={blockForm.reason} onChange={(e) => setBlockForm((f) => ({ ...f, reason: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateBlock} disabled={createBlock.isPending}>
              {createBlock.isPending ? "Salvando..." : "Bloquear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Modal de Novo Agendamento ─────────────────────────────────────────────────────── */}
      <Dialog open={newApptOpen} onOpenChange={(o) => { if (!o) setNewApptOpen(false); }}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-lg max-h-[90dvh] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-6 pt-6 pb-4 shrink-0 border-b border-border">
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-4 h-4" /> Novo Agendamento
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-4 space-y-4">
            {/* Cliente com autocomplete */}
            {/* Avatar do cliente selecionado */}
            {selectedClientPhoto && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <img
                  src={selectedClientPhoto}
                  alt={newForm.clientName}
                  className="w-12 h-12 rounded-full object-cover border-2 border-amber-400 flex-shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-sm text-amber-900 truncate">{newForm.clientName}</p>
                  {newForm.clientPhone && <p className="text-xs text-amber-700 truncate">{newForm.clientPhone}</p>}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedClientPhoto(null);
                    setClientSearchQuery("");
                    setNewForm((f) => ({ ...f, clientName: "", clientPhone: "", clientEmail: "" }));
                  }}
                  className="text-amber-500 hover:text-amber-700 flex-shrink-0"
                  title="Limpar cliente selecionado"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            {/* No mobile: botão que abre bottom sheet de busca de cliente */}
            {isMobile ? (
              <div>
                <Label>Cliente *</Label>
                <button
                  type="button"
                  onClick={() => { setClientSheetSearch(newForm.clientName); setClientSheetOpen(true); }}
                  className="mt-1 w-full flex items-center gap-3 px-3 py-3 rounded-lg border border-input bg-background hover:bg-accent/50 transition-colors text-left"
                >
                  {newForm.clientName ? (
                    <>
                      {selectedClientPhoto ? (
                        <img src={selectedClientPhoto} alt={newForm.clientName} className="w-9 h-9 rounded-full object-cover shrink-0" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <span className="text-sm font-bold text-primary">{newForm.clientName.charAt(0).toUpperCase()}</span>
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground truncate">{newForm.clientName}</p>
                        {newForm.clientPhone && <p className="text-xs text-muted-foreground truncate flex items-center gap-1"><Phone className="w-3 h-3" />{newForm.clientPhone}</p>}
                      </div>
                      <button type="button" onClick={(e) => { e.stopPropagation(); setSelectedClientPhoto(null); setClientSearchQuery(""); setNewForm((f) => ({ ...f, clientName: "", clientPhone: "", clientEmail: "" })); }} className="text-muted-foreground shrink-0">
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                        <Search className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <span className="text-sm text-muted-foreground">Buscar cliente...</span>
                    </>
                  )}
                </button>
                {/* Campo de telefone sempre visível no mobile */}
                <div className="mt-3">
                  <Label>Telefone *</Label>
                  <Input
                    value={newForm.clientPhone}
                    onChange={(e) => {
                      const val = e.target.value;
                      setNewForm((f) => ({ ...f, clientPhone: val }));
                      setPhoneSearchQuery(val);
                    }}
                    className="mt-1"
                    placeholder="(11) 99999-9999"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div ref={clientSearchRef} className="relative">
                  <Label>Nome do cliente *</Label>
                  <Input
                    value={newForm.clientName}
                    onChange={(e) => {
                      const val = e.target.value;
                      setNewForm((f) => ({ ...f, clientName: val }));
                      setClientSearchQuery(val);
                      setSelectedClientPhoto(null);
                      setShowClientSuggestions(val.length >= 2);
                    }}
                    onFocus={() => {
                      if (newForm.clientName.length >= 2) setShowClientSuggestions(true);
                    }}
                    className="mt-1"
                    placeholder="Nome completo"
                    autoComplete="off"
                  />
                  {showClientSuggestions && clientSuggestions.length > 0 && (
                    <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-border rounded-lg shadow-lg overflow-hidden max-h-56 overflow-y-auto">
                      {clientSuggestions.map((c: any, idx: number) => (
                        <button key={idx} type="button" className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-amber-50 text-left transition-colors" onMouseDown={(e) => { e.preventDefault(); selectClientSuggestion(c); }}>
                          {c.photoUrl ? <img src={c.photoUrl} alt={c.name} className="w-8 h-8 rounded-full object-cover border border-amber-300 flex-shrink-0" /> : <div className="w-8 h-8 rounded-full bg-amber-100 border border-amber-300 flex items-center justify-center flex-shrink-0"><span className="text-xs font-bold text-amber-700">{(c.name ?? "?").charAt(0).toUpperCase()}</span></div>}
                          <div className="min-w-0 flex-1"><p className="text-sm font-medium text-gray-900 truncate">{c.name}</p>{c.phone && <p className="text-xs text-gray-500 truncate">{c.phone}</p>}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div ref={phoneSearchRef} className="relative">
                  <Label>Telefone *</Label>
                  <Input value={newForm.clientPhone} onChange={(e) => { const val = e.target.value; setNewForm((f) => ({ ...f, clientPhone: val })); setPhoneSearchQuery(val); setSelectedClientPhoto(null); setShowPhoneSuggestions(val.replace(/\D/g, "").length >= 3); }} onFocus={() => { if (newForm.clientPhone.replace(/\D/g, "").length >= 3) setShowPhoneSuggestions(true); }} className="mt-1" placeholder="(11) 99999-9999" autoComplete="off" />
                  {showPhoneSuggestions && phoneSuggestions.length > 0 && (
                    <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-border rounded-lg shadow-lg overflow-hidden max-h-56 overflow-y-auto">
                      {phoneSuggestions.map((c: any, idx: number) => (
                        <button key={idx} type="button" className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-amber-50 text-left transition-colors" onMouseDown={(e) => { e.preventDefault(); selectClientSuggestion(c); }}>
                          {c.photoUrl ? <img src={c.photoUrl} alt={c.name} className="w-8 h-8 rounded-full object-cover border border-amber-300 flex-shrink-0" /> : <div className="w-8 h-8 rounded-full bg-amber-100 border border-amber-300 flex items-center justify-center flex-shrink-0"><span className="text-xs font-bold text-amber-700">{(c.name ?? "?").charAt(0).toUpperCase()}</span></div>}
                          <div className="min-w-0 flex-1"><p className="text-sm font-medium text-gray-900 truncate">{c.name}</p>{c.phone && <p className="text-xs text-gray-500 truncate">{c.phone}</p>}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            <div>
              <Label>E-mail</Label>
              <Input type="email" value={newForm.clientEmail} onChange={(e) => setNewForm((f) => ({ ...f, clientEmail: e.target.value }))} className="mt-1" placeholder="cliente@email.com" />
            </div>

            {/* Profissional e Serviço */}
            <div className={`grid grid-cols-1 gap-3 ${newForm.serviceId ? "sm:grid-cols-2" : ""}`}>
              <div className={newForm.serviceId ? "order-1" : ""}>
                <Label>Serviço *</Label>
                {isMobile ? (
                  <button
                    type="button"
                    onClick={() => { setServiceSheetSearch(""); setServiceSheetOpen(true); }}
                    className="mt-1 w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-input bg-background hover:bg-accent/50 transition-colors text-left"
                  >
                    {newForm.serviceId ? (
                      <>
                        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <Clock className="w-3.5 h-3.5 text-primary" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-foreground truncate">{services.find((s: any) => String(s.id) === newForm.serviceId)?.name ?? "Serviço"}</p>
                          {services.find((s: any) => String(s.id) === newForm.serviceId)?.durationMinutes && (
                            <p className="text-xs text-muted-foreground">{services.find((s: any) => String(s.id) === newForm.serviceId)?.durationMinutes} min</p>
                          )}
                        </div>
                        <X className="w-4 h-4 text-muted-foreground shrink-0" onClick={(e) => { e.stopPropagation(); setNewForm((f) => ({ ...f, serviceId: "", professionalId: linkedProfId ? String(linkedProfId) : "", totalPrice: "", endTime: "" })); }} />
                      </>
                    ) : (
                      <>
                        <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center shrink-0">
                          <Search className="w-3.5 h-3.5 text-muted-foreground" />
                        </div>
                        <span className="text-sm text-muted-foreground">Selecionar serviço...</span>
                      </>
                    )}
                  </button>
                ) : (
                  <Select value={newForm.serviceId} onValueChange={(v) => {
                    const selectedService = services.find((s: any) => String(s.id) === v);
                    setNewForm((f) => {
                      const updates: Partial<typeof f> = {
                        serviceId: v,
                        professionalId: linkedProfId ? String(linkedProfId) : "",
                      };
                      if (selectedService) {
                        if (selectedService.price != null) updates.totalPrice = Number(selectedService.price).toFixed(2);
                        if (f.startTime) {
                          // Soma duração do serviço principal + extras já adicionados
                          updates.endTime = calcEndTimeFromServices(f.startTime, v, newExtraServices);
                        }
                      }
                      return { ...f, ...updates };
                    });
                  }}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      {newServiceOptions.map((s: any) => (
                        <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <p className="mt-1 text-xs text-muted-foreground">Após escolher o serviço, a lista mostra somente os profissionais habilitados.</p>
              </div>
              {newForm.serviceId && (
                <div className="order-2">
                  <Label>Profissional habilitado *</Label>
                  {linkedProfId ? (
                    <div className="mt-1 flex items-center gap-1.5 px-3 py-2 rounded-md border border-border bg-muted/40 text-sm">
                      <Users className="w-3.5 h-3.5 text-muted-foreground" />
                      {(professionals as any[]).find((p: any) => p.id === linkedProfId)?.name ?? "Meu profissional"}
                    </div>
                  ) : (
                    <Select value={newForm.professionalId} onValueChange={(v) => setNewForm((f) => ({
                      ...f,
                      professionalId: v,
                    }))}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>
                        {newProfessionalOptions.map((p: any) => (
                          <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}
            </div>

            {/* Data e Horários */}
            <div className="space-y-3">
              <div>
                <Label>Data *</Label>
                <Input type="date" value={newForm.date} onChange={(e) => setNewForm((f) => ({ ...f, date: e.target.value }))} className="mt-1 w-full" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="whitespace-nowrap">Início *</Label>
                  <Input type="time" value={newForm.startTime} onChange={(e) => {
                    const newStartTime = e.target.value;
                    setNewForm((f) => {
                      const updates: Partial<typeof f> = { startTime: newStartTime };
                      if (f.serviceId) {
                        updates.endTime = calcEndTimeFromServices(newStartTime, f.serviceId, newExtraServices);
                      }
                      return { ...f, ...updates };
                    });
                  }} className="mt-1 w-full" />
                </div>
                <div>
                  <Label className="whitespace-nowrap">Fim *</Label>
                  <Input type="time" value={newForm.endTime} onChange={(e) => setNewForm((f) => ({ ...f, endTime: e.target.value }))} className="mt-1 w-full" />
                </div>
              </div>
            </div>

            {/* Serviços Extras - Novo Agendamento */}
            {newExtraServices.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Serviços adicionais</Label>
                {newExtraServices.map((es, idx) => (
                  <div key={idx} className="space-y-1.5 p-2 rounded-md border border-border/50 bg-secondary/20">
                    <div className="flex items-center gap-2">
                      <Select
                        value={es.serviceId}
                        onValueChange={(v) => {
                          const svc = services.find((s: any) => String(s.id) === v);
                          const updatedExtras = newExtraServices.map((item, i) =>
                            i === idx ? { ...item, serviceId: v, price: svc ? String(svc.price ?? 0) : item.price, durationMinutes: svc?.durationMinutes ?? 60 } : item
                          );
                          setNewExtraServices(updatedExtras);
                          // Recalcular horário fim com todos os serviços
                          if (newForm.serviceId && newForm.startTime) {
                            setNewForm(f => ({ ...f, endTime: calcEndTimeFromServices(f.startTime, f.serviceId, updatedExtras) }));
                          }
                        }}
                      >
                        <SelectTrigger className="flex-1"><SelectValue placeholder="Selecione o serviço" /></SelectTrigger>
                        <SelectContent>
                          {eligibleServicesForProfessional(es.professionalId ?? newAppointmentProfessionalId).map((s: any) => (
                            <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={es.price}
                        onChange={(e) => setNewExtraServices(prev => prev.map((item, i) => i === idx ? { ...item, price: e.target.value } : item))}
                        className="w-24"
                        placeholder="R$"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setNewExtraServices(prev => prev.filter((_, i) => i !== idx))}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <Select
                      value={es.professionalId ?? "none"}
                      onValueChange={(v) => setNewExtraServices(prev => prev.map((item, i) => i === idx ? { ...item, professionalId: v === "none" ? undefined : v } : item))}
                    >
                      <SelectTrigger className="w-full h-7 text-xs">
                        <SelectValue placeholder="Profissional executor (opcional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Mesmo do agendamento</SelectItem>
                        {eligibleProfessionalsForService(es.serviceId).map((p: any) => (
                          <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full border-dashed text-muted-foreground hover:text-foreground"
              onClick={() => setNewExtraServices(prev => [...prev, { serviceId: "", price: "", durationMinutes: 60 }])}
            >
              <Plus className="w-4 h-4 mr-1" /> Adicionar serviço
            </Button>
            {/* Valor */}
            <div>
              <Label>Valor (R$)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={
                  newExtraServices.length > 0
                    ? (() => {
                        const primaryPrice = newForm.serviceId
                          ? Number(services.find((s: any) => String(s.id) === newForm.serviceId)?.price ?? 0)
                          : 0;
                        const extrasTotal = newExtraServices
                          .filter(es => es.serviceId)
                          .reduce((sum, es) => {
                            const svc = services.find((s: any) => String(s.id) === es.serviceId);
                            return sum + Number(es.price || svc?.price || 0);
                          }, 0);
                        return (primaryPrice + extrasTotal).toFixed(2);
                      })()
                    : newForm.totalPrice
                }
                onChange={(e) => newExtraServices.length === 0 && setNewForm((f) => ({ ...f, totalPrice: e.target.value }))}
                readOnly={newExtraServices.length > 0}
                className="mt-1"
                placeholder="Preenchido ao selecionar o serviço"
              />
            </div>

            {/* Observações */}
            <div>
              <Label>Observações</Label>
              <Textarea value={newForm.notes} onChange={(e) => setNewForm((f) => ({ ...f, notes: e.target.value }))} className="mt-1 resize-none" rows={3} placeholder="Informações adicionais..." />
            </div>

            {/* Aviso de conflito */}
            {conflictWarning && newApptOpen && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
                <div className="flex items-start gap-2">
                  <span className="text-amber-600 text-lg leading-none">⚠️</span>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-800">Conflito de Horário Detectado</p>
                    <p className="text-xs text-amber-700 mt-0.5">{conflictWarning}</p>
                    <p className="text-xs text-amber-600 mt-1">Clique em &quot;Criar Mesmo Assim&quot; para confirmar ou ajuste o horário.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="px-6 py-4 shrink-0 border-t border-border bg-background">
            <Button variant="outline" onClick={() => { setNewApptOpen(false); setConflictWarning(null); setConflictChecked(false); }}>Cancelar</Button>
            {conflictWarning && newApptOpen ? (
              <>
                <Button variant="outline" onClick={() => { setConflictWarning(null); setConflictChecked(false); }}>Ajustar Horário</Button>
                <Button variant="destructive" onClick={handleCreateAppt} disabled={createApptMutation.isPending}>
                  {createApptMutation.isPending ? "Criando..." : "Criar Mesmo Assim"}
                </Button>
              </>
            ) : (
              <Button onClick={handleCreateAppt} disabled={createApptMutation.isPending}>
                {createApptMutation.isPending ? "Criando..." : "Criar Agendamento"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Modal de Edição de Agendamento (duplo clique) ─────────────────────────────────── */}
      <Dialog open={!!editAppt} onOpenChange={(o) => { if (!o) setEditAppt(null); }}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-lg max-h-[90dvh] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="px-4 sm:px-6 pt-5 pb-4 shrink-0 border-b border-border">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Edit2 className="w-4 h-4" /> Editar Agendamento #{editAppt?.id}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-6 py-4 space-y-4">
            {/* Cliente */}
            {/* Avatar do cliente */}
            {editAppt?.clientPhotoUrl && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <img
                  src={editAppt.clientPhotoUrl}
                  alt={editAppt.clientName ?? "Cliente"}
                  className="w-12 h-12 rounded-full object-cover border-2 border-amber-400 flex-shrink-0"
                />
                <div className="min-w-0">
                  <p className="font-semibold text-sm text-amber-900 truncate">{editAppt.clientName}</p>
                  {editAppt.clientPhone && <p className="text-xs text-amber-700 truncate">{editAppt.clientPhone}</p>}
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Nome do cliente *</Label>
                <Input value={editForm.clientName} onChange={(e) => setEditForm((f) => ({ ...f, clientName: e.target.value }))} className="mt-1" placeholder="Nome completo" />
              </div>
              <div>
                <Label>Telefone</Label>
                <Input value={editForm.clientPhone} onChange={(e) => setEditForm((f) => ({ ...f, clientPhone: e.target.value }))} className="mt-1" placeholder="(11) 99999-9999" />
              </div>
            </div>
            <div>
              <Label>E-mail</Label>
              <Input type="email" value={editForm.clientEmail} onChange={(e) => setEditForm((f) => ({ ...f, clientEmail: e.target.value }))} className="mt-1" placeholder="cliente@email.com" />
            </div>

            {/* Profissional e Serviço */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="order-2">
                <Label>Profissional habilitado</Label>
                <Select value={editForm.professionalId} onValueChange={(v) => setEditForm((f) => ({
                  ...f,
                  professionalId: v,
                  serviceId: isProfessionalEligibleForService(v, f.serviceId) ? f.serviceId : "",
                }))}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {editProfessionalOptions.map((p: any) => (
                      <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="order-1">
                <Label>Serviço</Label>
                <Select value={editForm.serviceId} onValueChange={(v) => {
                    const selectedService = services.find((s: any) => String(s.id) === v);
                    setEditForm((f) => {
                      const updates: Partial<typeof f> = { serviceId: v };
                      if (!isProfessionalEligibleForService(f.professionalId, v)) {
                        updates.professionalId = "";
                      }
                      if (selectedService) {
                        // Atualizar preço com o valor do serviço
                        if (selectedService.price != null) {
                          updates.totalPrice = Number(selectedService.price).toFixed(2);
                        }
                      }
                      // Recalcular horário de fim somando duração de todos os serviços (principal + extras)
                      if (f.startTime) {
                        updates.endTime = calcEndTimeFromServices(f.startTime, v, extraServices);
                      }
                      return { ...f, ...updates };
                    });
                  }}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    {editServiceOptions.map((s: any) => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="mt-1 text-xs text-muted-foreground">A lista de profissionais é limitada aos executores deste serviço.</p>
              </div>
            </div>

            {/* Serviços Extras */}
            {extraServices.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Serviços adicionais</Label>
                {extraServices.map((es, idx) => (
                  <div key={idx} className="space-y-1.5 p-2 rounded-md border border-border/50 bg-secondary/20">
                    <div className="flex items-center gap-2">
                      <Select
                        value={es.serviceId}
                        onValueChange={(v) => {
                          const svc = services.find((s: any) => String(s.id) === v);
                          const updatedExtras = extraServices.map((item, i) =>
                            i === idx ? { ...item, serviceId: v, price: svc ? String(svc.price ?? 0) : item.price, durationMinutes: svc?.durationMinutes ?? 60 } : item
                          );
                          setExtraServices(updatedExtras);
                          // Recalcular horário de fim com os extras atualizados
                          if (editForm.startTime) {
                            setEditForm(f => ({ ...f, endTime: calcEndTimeFromServices(f.startTime, f.serviceId, updatedExtras) }));
                          }
                        }}
                      >
                        <SelectTrigger className="flex-1"><SelectValue placeholder="Selecione o serviço" /></SelectTrigger>
                        <SelectContent>
                          {eligibleServicesForProfessional(es.professionalId ?? editForm.professionalId).map((s: any) => (
                            <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={es.price}
                        onChange={(e) => setExtraServices(prev => prev.map((item, i) => i === idx ? { ...item, price: e.target.value } : item))}
                        className="w-24"
                        placeholder="R$"
                      />
                      {confirmRemoveServiceIdx === idx ? (
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-9 px-2 text-xs text-red-600 hover:bg-red-50 font-medium"
                            onClick={() => {
                            const updatedExtras = extraServices.filter((_, i) => i !== idx);
                            setExtraServices(updatedExtras);
                            setConfirmRemoveServiceIdx(null);
                            // Recalcular horário de fim sem o serviço removido
                            if (editForm.startTime) {
                              setEditForm(f => ({ ...f, endTime: calcEndTimeFromServices(f.startTime, f.serviceId, updatedExtras) }));
                            }
                          }}
                          >
                            Remover
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-9 px-2 text-xs text-muted-foreground"
                            onClick={() => setConfirmRemoveServiceIdx(null)}
                          >
                            Cancelar
                          </Button>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setConfirmRemoveServiceIdx(idx)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <Select
                      value={es.professionalId ?? "none"}
                      onValueChange={(v) => setExtraServices(prev => prev.map((item, i) => i === idx ? { ...item, professionalId: v === "none" ? undefined : v } : item))}
                    >
                      <SelectTrigger className="w-full h-7 text-xs">
                        <SelectValue placeholder="Profissional executor (opcional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Mesmo do agendamento</SelectItem>
                        {eligibleProfessionalsForService(es.serviceId).map((p: any) => (
                          <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full border-dashed text-muted-foreground hover:text-foreground"
              onClick={() => setExtraServices(prev => [...prev, { serviceId: "", price: "", durationMinutes: 60 }])}
            >
              <Plus className="w-4 h-4 mr-1" /> Adicionar serviço
            </Button>
            {/* Produtos Vendidos */}
            {editAppt?.id && (
              <div className="border rounded-lg p-3 bg-amber-50/40">
                <h4 className="text-sm font-semibold text-amber-900 mb-3 flex items-center gap-1">
                  <Package className="w-4 h-4" /> Produtos Vendidos
                </h4>
                {/* Seletor de produto */}
                <div className="flex gap-2 mb-3">
                  <Select value={selectedProductId} onValueChange={setSelectedProductId}>
                    <SelectTrigger className="flex-1 text-xs h-8">
                      <SelectValue placeholder="Selecionar produto..." />
                    </SelectTrigger>
                    <SelectContent>
                      {(allProductsList as any[]).filter((p: any) => p.stockQty > 0).map((p: any) => (
                        <SelectItem key={p.id} value={String(p.id)}>
                          {p.name} — R$ {parseFloat(String(p.salePrice) || '0').toFixed(2)} (estoque: {p.stockQty})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    className="h-8 text-xs"
                    disabled={!selectedProductId}
                    onClick={() => {
                      const prod = (allProductsList as any[]).find((p: any) => String(p.id) === selectedProductId);
                      if (!prod) return;
                      const existing = apptProducts.find(ap => ap.productId === prod.id);
                      if (existing) {
                        setApptProducts(prev => prev.map(ap => ap.productId === prod.id ? { ...ap, quantity: ap.quantity + 1 } : ap));
                      } else {
                        setApptProducts(prev => [...prev, { productId: prod.id, productName: prod.name, quantity: 1, unitPrice: parseFloat(String(prod.salePrice)) || 0 }]);
                      }
                      setSelectedProductId("");
                    }}
                  >
                    <Plus className="w-3 h-3 mr-1" /> Adicionar
                  </Button>
                </div>
                {/* Lista de produtos adicionados */}
                {apptProducts.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-2">Nenhum produto adicionado</p>
                ) : (
                  <div className="space-y-1.5 mb-3">
                    {apptProducts.map((ap) => (
                      <div key={ap.productId} className="flex items-center gap-2 bg-white rounded border px-2 py-1.5">
                        <span className="flex-1 text-xs font-medium truncate">{ap.productName}</span>
                        <span className="text-xs text-muted-foreground">R$ {ap.unitPrice.toFixed(2)}</span>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="outline" className="h-5 w-5 p-0 text-xs" onClick={() => setApptProducts(prev => prev.map(p => p.productId === ap.productId ? { ...p, quantity: Math.max(1, p.quantity - 1) } : p))}>-</Button>
                          <span className="text-xs w-4 text-center">{ap.quantity}</span>
                          <Button size="sm" variant="outline" className="h-5 w-5 p-0 text-xs" onClick={() => setApptProducts(prev => prev.map(p => p.productId === ap.productId ? { ...p, quantity: p.quantity + 1 } : p))}>+</Button>
                        </div>
                        <span className="text-xs font-semibold w-16 text-right">R$ {(ap.unitPrice * ap.quantity).toFixed(2)}</span>
                        <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-destructive" onClick={() => setApptProducts(prev => prev.filter(p => p.productId !== ap.productId))}><X className="w-3 h-3" /></Button>
                      </div>
                    ))}
                    <div className="flex justify-between items-center pt-1 border-t">
                      <span className="text-xs text-muted-foreground">Total produtos</span>
                      <span className="text-xs font-bold">R$ {apptProducts.reduce((s, p) => s + p.unitPrice * p.quantity, 0).toFixed(2)}</span>
                    </div>
                  </div>
                )}
                <Button
                  size="sm"
                  className="w-full h-7 text-xs"
                  variant="outline"
                  onClick={() => updateApptProductsMutation.mutate({ appointmentId: editAppt!.id, products: apptProducts })}
                  disabled={updateApptProductsMutation.isPending}
                >
                  {updateApptProductsMutation.isPending ? "Salvando..." : "Salvar Produtos"}
                </Button>
              </div>
            )}
            {/* Data e Horários */}
            <div className="space-y-3">
              <div>
                <Label>Data *</Label>
                <Input type="date" value={editForm.startDate} onChange={(e) => setEditForm((f) => ({ ...f, startDate: e.target.value }))} className="mt-1 w-full" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Início *</Label>
                  <Input type="time" value={editForm.startTime} onChange={(e) => setEditForm((f) => ({ ...f, startTime: e.target.value }))} className="mt-1 w-full" />
                </div>
                <div>
                  <Label>Fim *</Label>
                  <Input type="time" value={editForm.endTime} onChange={(e) => setEditForm((f) => ({ ...f, endTime: e.target.value }))} className="mt-1 w-full" />
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Status</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pendente</SelectItem>
                    <SelectItem value="confirmed">Confirmado</SelectItem>
                    <SelectItem value="in_service">Em atendimento</SelectItem>
                    <SelectItem value="completed">Concluído</SelectItem>
                    <SelectItem value="cancelled">Cancelado</SelectItem>
                    <SelectItem value="no_show">Não compareceu</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Valor (R$)</Label>
                {(() => {
                  const basePrice = editForm.serviceId
                    ? Number((services as any[]).find((s: any) => String(s.id) === editForm.serviceId)?.price ?? 0)
                    : 0;
                  const currentVal = Number(editForm.totalPrice) || 0;
                  const isBelowBase = basePrice > 0 && currentVal < basePrice && !editPinApproved;
                  return (
                    <>
                      <Input
                        type="number" step="0.01" min="0"
                        value={editForm.totalPrice}
                        onChange={(e) => {
                          setEditForm((f) => ({ ...f, totalPrice: e.target.value }));
                          setEditPinApproved(false);
                          setUserEditedPrice(true); // marca que o usuário editou manualmente
                        }}
                        className={`mt-1 ${isBelowBase ? 'border-amber-500 focus-visible:ring-amber-500' : ''}`}
                        placeholder="0.00"
                      />
                      {basePrice > 0 && (
                        <p className="text-xs mt-1 text-muted-foreground">
                          A partir de R$ {basePrice.toFixed(2)}
                        </p>
                      )}
                      {isBelowBase && (
                        <p className="text-xs mt-1 text-amber-600 font-medium">
                          ⚠️ Valor abaixo do mínimo — requer autorização PIN ao salvar
                        </p>
                      )}
                      {editPinApproved && currentVal < basePrice && (
                        <p className="text-xs mt-1 text-green-600 font-medium">
                          ✅ Desconto autorizado
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Pagamento */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Status do Pagamento</Label>
                <Select value={editForm.paymentStatus} onValueChange={(v) => setEditForm((f) => ({ ...f, paymentStatus: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unpaid">Não pago</SelectItem>
                    <SelectItem value="paid">Pago</SelectItem>
                    <SelectItem value="refunded">Reembolsado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Forma de Pagamento</Label>
                <Select value={editForm.paymentMethod} onValueChange={(v) => setEditForm((f) => ({ ...f, paymentMethod: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dinheiro">Dinheiro</SelectItem>
                    <SelectItem value="pix">Pix</SelectItem>
                    <SelectItem value="credito">Cartão de crédito</SelectItem>
                    <SelectItem value="debito">Cartão de débito</SelectItem>
                    <SelectItem value="voucher">Voucher</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Observações */}
            <div>
              <Label>Observações</Label>
              <Textarea value={editForm.notes} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))} className="mt-1 resize-none" rows={3} placeholder="Informações adicionais sobre o agendamento..." />
            </div>

            {/* Toggle notificar cliente */}
            {editForm.clientPhone && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200">
                <MessageCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <label className="flex items-center gap-2 cursor-pointer flex-1 text-sm text-blue-800">
                  <input
                    type="checkbox"
                    checked={notifyClient}
                    onChange={(e) => setNotifyClient(e.target.checked)}
                    className="rounded"
                  />
                  Notificar cliente por WhatsApp ao salvar
                </label>
              </div>
            )}

            {/* Confirmar cancelamento (admin) */}
            {confirmCancel && !isReceptionist && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
                <p className="font-medium mb-2">Tem certeza que deseja cancelar este agendamento?</p>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setConfirmCancel(false)}>Não</Button>
                  <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => cancelApptMutation.mutate({ id: editAppt!.id })} disabled={cancelApptMutation.isPending}>
                    {cancelApptMutation.isPending ? "Cancelando..." : "Sim, cancelar"}
                  </Button>
                </div>
              </div>
            )}
            {/* Solicitar cancelamento (recepcionista) */}
            {confirmCancelRequest && isReceptionist && (
              <div className="p-3 rounded-lg bg-orange-50 border border-orange-200 text-sm text-orange-900">
                <p className="font-medium mb-2">Solicitar cancelamento deste agendamento?</p>
                <p className="text-xs text-orange-700 mb-2">A solicitação será enviada ao administrador para aprovação.</p>
                <Textarea
                  placeholder="Motivo do cancelamento (opcional)..."
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  rows={2}
                  className="mb-2 text-sm"
                />
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setConfirmCancelRequest(false); setCancelReason(""); }}>Voltar</Button>
                  <Button size="sm" className="bg-orange-600 hover:bg-orange-700 text-white" onClick={() => requestCancelMutation.mutate({ appointmentId: editAppt!.id, reason: cancelReason || undefined })} disabled={requestCancelMutation.isPending}>
                    {requestCancelMutation.isPending ? "Enviando..." : "Enviar solicitação"}
                  </Button>
                </div>
              </div>
            )}

            {/* Fotos do atendimento */}
            {editAppt?.id && (
              <div className="border rounded-lg p-3 bg-amber-50/40">
                <h4 className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-1">
                  <Camera className="w-4 h-4" /> Fotos do Atendimento
                </h4>
                <AppointmentPhotoGallery appointmentId={editAppt.id} salonId={mySalon?.id ?? 0} canEdit={true} />
              </div>
            )}

            {/* Histórico de alterações */}
            <AppointmentHistorySection appointmentId={editAppt?.id} />
          </div>
          <DialogFooter className="flex flex-col gap-2 w-full px-4 sm:px-6 py-3 sm:py-4 shrink-0 border-t border-border bg-background">
            {/* Linha 1: Cancelar Agendamento */}
            {/* Botão de cancelar: admin cancela direto, recepcionista solicita */}
            {isReceptionist ? (
              <Button variant="outline" className="text-orange-600 border-orange-300 hover:bg-orange-50 w-full sm:w-auto" onClick={() => setConfirmCancelRequest(true)} disabled={confirmCancelRequest}>
                <Trash2 className="w-4 h-4 mr-1" /> Solicitar Cancelamento
              </Button>
            ) : (
              <Button variant="outline" className="text-red-600 border-red-300 hover:bg-red-50 w-full sm:w-auto" onClick={() => setConfirmCancel(true)} disabled={confirmCancel}>
                <Trash2 className="w-4 h-4 mr-1" /> Cancelar Agendamento
              </Button>
            )}
            {/* Linha 2: Imprimir | Fechar | Salvar */}
            <div className="flex flex-row gap-2 justify-end w-full flex-wrap">
            <Button
              variant="outline"
              className="border-primary/30 text-primary hover:bg-primary/10"
              disabled={!editAppt?.id || registerAgendaArrival.isPending}
              onClick={() => {
                if (!editAppt?.id) return;
                handleAgendaArrival(editAppt.id);
              }}
            >
              <Users className="w-4 h-4 mr-1" />
              {registerAgendaArrival.isPending ? "Enviando..." : "Enviar para fila"}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                if (!editAppt) return;
                const salonName = mySalon?.name ?? "Beleza & Bronze";
                const salonPhone = mySalon?.phone ?? "";
                const primarySvc = services.find((s: any) => String(s.id) === editForm.serviceId);
                const allSvcs = [
                  ...(primarySvc ? [{ name: primarySvc.name, price: editForm.totalPrice && extraServices.length === 0 ? editForm.totalPrice : String(primarySvc.price ?? 0) }] : []),
                  ...extraServices.filter(es => es.serviceId).map(es => {
                    const svc = services.find((s: any) => String(s.id) === es.serviceId);
                    return { name: svc?.name ?? "Serviço", price: es.price };
                  }),
                ];
                const total = editForm.totalPrice ? Number(editForm.totalPrice).toFixed(2) : "--";
                const startDt = editAppt.startTime ? new Date(editAppt.startTime) : null;
                const dateStr = startDt ? startDt.toLocaleDateString("pt-BR") : editForm.startDate;
                const timeStr = startDt ? startDt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : editForm.startTime;
                const prof = (professionals as any[]).find((p: any) => String(p.id) === editForm.professionalId);
                // Gerar HTML da comanda e abrir em nova aba com botão de impressão
                // Usar blob URL para evitar travamento no iPad com window.print() direto
                const comandaHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>Comanda #${editAppt.id}</title>
                  <style>
                    @page { size: 80mm auto; margin: 2mm; }
                    * { box-sizing: border-box; }
                    html, body { width: 76mm; min-height: 0; }
                    body { font-family: Arial, Helvetica, sans-serif; font-size: 10.5pt; line-height: 1.22; margin: 0; padding: 0; color: #000; background: #fff; font-weight: 500; }
                    h1 { font-size: 15pt; line-height: 1.05; margin: 0 0 2px; font-weight: 700; }
                    .sub, .sub2 { color: #000; font-size: 8.5pt; margin-bottom: 3px; }
                    .divider { border-top: 1px dashed #000; margin: 5px 0; }
                    .row { display: flex; justify-content: space-between; gap: 8px; margin: 2px 0; }
                    .row > :last-child { text-align: right; }
                    .label { color: #000; }
                    .total { font-weight: 700; font-size: 13pt; }
                    .footer { margin-top: 7px; text-align: center; font-size: 8.5pt; color: #000; }
                    .btn-bar { display: flex; gap: 10px; justify-content: center; margin: 20px 0 8px; }
                    .btn { padding: 10px 24px; border: none; border-radius: 6px; font-size: 14px; cursor: pointer; }
                    .btn-print { background: #b8860b; color: #fff; }
                    .btn-close { background: #eee; color: #333; }
                    @media print { .btn-bar { display: none; } html, body { width: 76mm; padding: 0; } }
                  </style></head><body>
                  <h1>${salonName}</h1>
                  ${salonPhone ? `<div class="sub2">${salonPhone}</div>` : ""}
                  <div class="sub">Comanda #${editAppt.id}</div>
                  <div class="divider"></div>
                  <div class="row"><span class="label">Cliente:</span><span>${editForm.clientName}</span></div>
                  ${editForm.clientPhone ? `<div class="row"><span class="label">Telefone:</span><span>${editForm.clientPhone}</span></div>` : ""}
                  <div class="row"><span class="label">Data:</span><span>${dateStr} às ${timeStr}</span></div>
                  <div class="row"><span class="label">Profissional:</span><span>${prof?.name ?? "--"}</span></div>
                  <div class="divider"></div>
                  <div style="margin-bottom:4px"><strong>Serviços</strong></div>
                  ${allSvcs.map(s => `<div class="row"><span>${s.name}</span><span>R$ ${Number(s.price).toFixed(2)}</span></div>`).join("")}
                  <div class="divider"></div>
                  <div class="row total"><span>Total</span><span>R$ ${total}</span></div>
                  ${editForm.paymentStatus === "paid" ? `<div class="row"><span class="label">Pagamento:</span><span>${editForm.paymentMethod || "Pago"}</span></div>` : ""}
                  ${editForm.notes ? `<div class="divider"></div><div><span class="label">Obs: </span>${editForm.notes}</div>` : ""}
                  <div class="footer">Obrigado pela preferência!</div>
                  <div class="btn-bar">
                    <button class="btn btn-print" onclick="window.print()">Imprimir</button>
                    <button class="btn btn-close" onclick="window.close()">Fechar</button>
                  </div>
                  </body></html>`;
                const blob = new Blob([comandaHtml], { type: "text/html;charset=utf-8" });
                const blobUrl = URL.createObjectURL(blob);
                window.open(blobUrl, "_blank");
              }}
            >
              <Printer className="w-4 h-4 mr-1" /> Imprimir Comanda
            </Button>
            <Button variant="outline" onClick={() => { setEditAppt(null); setConflictWarning(null); setConflictChecked(false); }}>Fechar</Button>
            {conflictWarning && !newApptOpen ? (
              <>
                <Button variant="outline" onClick={() => { setConflictWarning(null); setConflictChecked(false); }}>Ajustar Horário</Button>
                <Button variant="destructive" onClick={handleSaveEdit} disabled={editApptMutation.isPending}>
                  {editApptMutation.isPending ? "Salvando..." : "Salvar Mesmo Assim"}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => {
                  // Verificar se o valor está abaixo do preço base e PIN ainda não foi aprovado
                  const basePrice = editForm.serviceId
                    ? Number((services as any[]).find((s: any) => String(s.id) === editForm.serviceId)?.price ?? 0)
                    : 0;
                  const currentVal = Number(editForm.totalPrice) || 0;
                  if (basePrice > 0 && currentVal < basePrice && !editPinApproved) {
                    setEditPinOpen(true);
                  } else {
                    handleSaveEdit();
                  }
                }}
                disabled={editApptMutation.isPending}
              >
                {editApptMutation.isPending ? "Salvando..." : "Salvar Alterações"}
              </Button>
            )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ─── PIN Modal para desconto no modal de edição ────────────────────────────────────────────────────────────────────────────────────────────────────────────────────── */}
      <AuthPinModal
        open={editPinOpen}
        onClose={() => setEditPinOpen(false)}
        title="Autorizar Desconto"
        description="Valor abaixo do preço mínimo. Digite o PIN de autorização para aplicar o desconto."
        onConfirm={async (pin) => {
          await editVerifyPin.mutateAsync({ pin });
          setEditPinApproved(true);
          setEditPinOpen(false);
          setTimeout(() => handleSaveEdit(), 100);
        }}
      />
      {/* ─── Bottom Sheet: Busca de Cliente (Mobile) ─────────────────────────────────────────────────────────────────── */}
      <Sheet open={clientSheetOpen} onOpenChange={setClientSheetOpen}>
        <SheetContent side="bottom" className="h-[85vh] flex flex-col p-0 rounded-t-2xl">
          <SheetHeader className="px-4 pt-4 pb-2 border-b border-border shrink-0">
            <SheetTitle className="text-base flex items-center gap-2">
              <Search className="w-4 h-4 text-primary" /> Buscar Cliente
            </SheetTitle>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                ref={clientSheetInputRef}
                autoFocus
                value={clientSheetSearch}
                onChange={(e) => setClientSheetSearch(e.target.value)}
                placeholder="Nome ou telefone..."
                className="pl-9 h-11 text-base"
              />
              {clientSheetSearch && (
                <button onClick={() => setClientSheetSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto">
            {/* Opção de novo cliente */}
            {clientSheetSearch.length >= 2 && (
              <button
                type="button"
                className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-accent/50 transition-colors border-b border-border/50"
                onClick={() => {
                  setNewForm((f) => ({ ...f, clientName: clientSheetSearch, clientPhone: "", clientEmail: "" }));
                  setClientSearchQuery(clientSheetSearch);
                  setSelectedClientPhoto(null);
                  setClientSheetOpen(false);
                }}
              >
                <div className="w-10 h-10 rounded-full bg-primary/10 border-2 border-dashed border-primary/40 flex items-center justify-center shrink-0">
                  <Plus className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Novo cliente: <span className="text-primary">{clientSheetSearch}</span></p>
                  <p className="text-xs text-muted-foreground">Criar cliente sem cadastro prévio</p>
                </div>
              </button>
            )}
            {/* Lista de clientes encontrados */}
            {(() => {
              const sheetClients = sheetClientResults?.clients ?? [];
              if (clientSheetSearch.length < 2) {
                return (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <Search className="w-10 h-10 mb-3 opacity-30" />
                    <p className="text-sm">Digite o nome ou telefone do cliente</p>
                  </div>
                );
              }
              if (sheetClients.length === 0) {
                return (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <Users className="w-10 h-10 mb-3 opacity-30" />
                    <p className="text-sm">Nenhum cliente encontrado</p>
                    <p className="text-xs mt-1">Use a opção acima para criar um novo</p>
                  </div>
                );
              }
              return sheetClients.map((c: any, idx: number) => (
                <button
                  key={idx}
                  type="button"
                  className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-accent/50 transition-colors border-b border-border/30 last:border-0"
                  onClick={() => {
                    selectClientSuggestion(c);
                    setClientSheetOpen(false);
                  }}
                >
                  {c.photoUrl ? (
                    <img src={c.photoUrl} alt={c.name} className="w-10 h-10 rounded-full object-cover border-2 border-border shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <span className="text-base font-bold text-primary">{(c.name ?? "?").charAt(0).toUpperCase()}</span>
                    </div>
                  )}
                  <div className="min-w-0 flex-1 text-left">
                    <p className="text-sm font-semibold text-foreground truncate">{c.name}</p>
                    {c.phone && <p className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</p>}
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </button>
              ));
            })()}
          </div>
        </SheetContent>
      </Sheet>

      {/* ─── Bottom Sheet: Seleção de Serviço (Mobile) ─────────────────────────────────────────────────────────────── */}
      <Sheet open={serviceSheetOpen} onOpenChange={setServiceSheetOpen}>
        <SheetContent side="bottom" className="h-[80vh] flex flex-col p-0 rounded-t-2xl">
          <SheetHeader className="px-4 pt-4 pb-2 border-b border-border shrink-0">
            <SheetTitle className="text-base flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" /> Selecionar Serviço
            </SheetTitle>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                autoFocus
                value={serviceSheetSearch}
                onChange={(e) => setServiceSheetSearch(e.target.value)}
                placeholder="Buscar serviço..."
                className="pl-9 h-11 text-base"
              />
              {serviceSheetSearch && (
                <button onClick={() => setServiceSheetSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto">
            {(() => {
              const filtered = newServiceOptions.filter((s: any) =>
                !serviceSheetSearch || s.name.toLowerCase().includes(serviceSheetSearch.toLowerCase())
              );
              if (filtered.length === 0) {
                return (
                  <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <Clock className="w-10 h-10 mb-3 opacity-30" />
                    <p className="text-sm">Nenhum serviço encontrado</p>
                  </div>
                );
              }
              return filtered.map((s: any) => (
                <button
                  key={s.id}
                  type="button"
                  className={`w-full flex items-center gap-3 px-4 py-3.5 hover:bg-accent/50 transition-colors border-b border-border/30 last:border-0 ${
                    newForm.serviceId === String(s.id) ? "bg-primary/5" : ""
                  }`}
                  onClick={() => {
                    const selectedService = s;
                    setNewForm((f) => {
                      const updates: Partial<typeof f> = {
                        serviceId: String(s.id),
                        professionalId: linkedProfId ? String(linkedProfId) : "",
                      };
                      if (selectedService.price != null) updates.totalPrice = Number(selectedService.price).toFixed(2);
                      if (selectedService.durationMinutes && f.startTime) {
                        const [h, m] = f.startTime.split(":").map(Number);
                        const startMins = h * 60 + m;
                        const endMins = startMins + selectedService.durationMinutes;
                        const endH = Math.floor(endMins / 60) % 24;
                        const endM = endMins % 60;
                        updates.endTime = `${String(endH).padStart(2, "0")}:${String(endM).padStart(2, "0")}`;
                      }
                      return { ...f, ...updates };
                    });
                    setServiceSheetOpen(false);
                  }}
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                    newForm.serviceId === String(s.id) ? "bg-primary text-primary-foreground" : "bg-muted"
                  }`}>
                    <Clock className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1 text-left">
                    <p className="text-sm font-semibold text-foreground truncate">{s.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {s.durationMinutes && <span className="text-xs text-muted-foreground">{s.durationMinutes} min</span>}
                      {s.price != null && <span className="text-xs font-medium text-primary">R$ {Number(s.price).toFixed(2)}</span>}
                    </div>
                  </div>
                  {newForm.serviceId === String(s.id) && <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />}
                </button>
              ));
            })()}
          </div>
        </SheetContent>
      </Sheet>
      {/* ─── Quick-Add Bottom Sheet (mobile) ──────────────────────────────────────────────────── */}
      <Sheet open={!!quickAddSlot} onOpenChange={(o) => { if (!o) setQuickAddSlot(null); }}>
        <SheetContent side="bottom" className="rounded-t-2xl px-0 pb-safe">
          <div className="px-5 pt-2 pb-1">
            {/* Handle bar */}
            <div className="w-10 h-1 rounded-full bg-muted-foreground/20 mx-auto mb-4" />
            <SheetTitle className="text-base font-semibold flex items-center gap-2 mb-4">
              <Plus className="w-4 h-4 text-primary" />
              Novo Agendamento
            </SheetTitle>

            {/* Hora selecionada */}
            {quickAddSlot && (
              <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-primary/8 border border-primary/20">
                <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                  <Clock className="w-4 h-4 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Horário selecionado</p>
                  <p className="text-sm font-bold text-foreground">
                    {new Date(`${quickAddSlot.date}T00:00:00`).toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'short' })}
                    {' às '}
                    {String(quickAddSlot.hour).padStart(2, '0')}:{String(quickAddSlot.minute).padStart(2, '0')}
                  </p>
                </div>
                {/* Editar hora */}
                <div className="ml-auto flex items-center gap-1">
                  <button
                    type="button"
                    className="w-7 h-7 rounded-full bg-muted flex items-center justify-center"
                    onClick={() => setQuickAddSlot(s => s ? { ...s, minute: Math.max(0, s.minute - 15) === 0 && s.hour > (startHour) ? { ...s, hour: s.hour - 1, minute: 45 }.minute : Math.max(0, s.minute - 15) } : s)}
                    aria-label="-15 min"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    className="w-7 h-7 rounded-full bg-muted flex items-center justify-center"
                    onClick={() => setQuickAddSlot(s => s ? { ...s, minute: (s.minute + 15) % 60 === 0 ? 0 : (s.minute + 15), hour: (s.minute + 15) >= 60 ? Math.min(s.hour + 1, 23) : s.hour } : s)}
                    aria-label="+15 min"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}

            {/* Profissional */}
            {filteredProfessionals.length > 1 && quickAddSlot && !linkedProfId && (
              <div className="mb-3">
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Profissional</label>
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
                  {filteredProfessionals.map((prof: any) => (
                    <button
                      key={prof.id}
                      type="button"
                      onClick={() => setQuickAddSlot(s => s ? { ...s, profId: prof.id } : s)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium whitespace-nowrap shrink-0 transition-colors ${
                        quickAddSlot?.profId === prof.id
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background border-border text-foreground'
                      }`}
                    >
                      <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold shrink-0" style={{ backgroundColor: prof.color ?? '#C9A96E' }}>
                        {prof.avatarUrl ? <img src={prof.avatarUrl} alt={prof.name} className="w-full h-full object-cover rounded-full" /> : prof.name.charAt(0).toUpperCase()}
                      </div>
                      {prof.name.split(' ')[0]}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Botões */}
            <div className="flex gap-2 mt-2">
              <Button
                variant="outline"
                className="flex-1 h-11"
                onClick={() => setQuickAddSlot(null)}
              >
                Cancelar
              </Button>
              <Button
                className="flex-1 h-11 bg-amber-500 hover:bg-amber-400 text-white"
                onClick={confirmQuickAdd}
              >
                <Plus className="w-4 h-4 mr-1" />
                Continuar
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* ─── FAB: Novo Agendamento (apenas mobile) ──────────────────────────────────────────────────── */}
      {isMobile && (
        <button
          className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom))] right-4 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-amber-500 text-white shadow-lg shadow-amber-500/40 transition-all duration-150 hover:bg-amber-400 active:scale-95 active:bg-amber-600"
          onClick={openNewApptModal}
          aria-label="Novo agendamento"
        >
          <Plus className="w-7 h-7" />
        </button>
      )}
    </DndContext>
  );
}
